import React, { useState, useEffect } from 'react';
import {
  ChevronDown, ChevronUp, Edit2, Clock, MapPin, Phone,
  PlusCircle, Box as BoxIcon, Trash2, Edit, Truck,
  AlertCircle, ChevronRight, Archive, CheckCircle, ExternalLink,
  MessageCircle
} from 'lucide-react';
import { supabase } from './supabaseClient';
import { useTenant } from './context/TenantContext';
import EncomiendaWizard from './EncomiendaWizard';
import ReceiptAnalyzer from './components/ReceiptAnalyzer';
import { generateCosteoXLSX } from './utils/generateCosteoXLSX';
import { generateBoxesRutaXLSX } from './utils/generateBoxesRutaXLSX';
import { SV_DEPARTAMENTOS } from './utils/svDepartamentos';
import { insertBox, BOX_NUMBER_ERROR } from './utils/insertBox';
import CosteoPreview from './components/CosteoPreview';
import { scanBarcode } from './utils/scanBarcode';
import { generateFotosPDF, generateRecibosPDF } from './utils/generateManifestPDF';

// ── Tenant gate for Boxful features ──────────────────────────────────────────
const MAXI_ENVIOS_TENANT_ID = 'ad94ae3b-bb09-45cd-aa1f-187e8b58647d';

// ── Session helpers ───────────────────────────────────────────────────────────
const openManifestSession = async (supabase, manifestId, user) => {
  await supabase
    .from('manifest_sessions')
    .upsert({
      manifest_id: manifestId,
      user_id: user.id,
      username: user.username,
      opened_at: new Date().toISOString(),
      last_active: new Date().toISOString(),
    }, { onConflict: 'manifest_id,user_id' });
};

const closeManifestSession = async (supabase, manifestId, user) => {
  await supabase
    .from('manifest_sessions')
    .delete()
    .eq('manifest_id', manifestId)
    .eq('user_id', user.id);
};

// ── Manifest Status Bar ──────────────────────────────────────────────────────
const ManifestStatusBar = ({ currentManifest, shipments, encomiendas, boxes, onOpenCloseModal, showOpenManifestPreview, onToggleCosteoPreview, userPermissions, onRequestClose }) => {
  const manifestShipments = currentManifest
    ? shipments.filter(s => s.manifest_id === currentManifest.id)
    : [];
  // Ship count = casillero SHIP + all packages in COMPLETE encomiendas
  const encPkgCount = (encomiendas || []).reduce(
    (sum, e) => sum + (e.encomienda_packages?.length || 0), 0
  );
  const shipCount = manifestShipments.filter(s => s.shipping_status === 'Send Immediately').length + encPkgCount;
  const holdCount = manifestShipments.filter(s => s.shipping_status === 'Postpone to Next Delivery').length;
  const manifestBoxes = currentManifest
    ? (boxes || []).filter(b => b.manifest_id === currentManifest.id)
    : [];
  const totalBoxes = manifestBoxes.length;
  const totalBoxLbs = manifestBoxes.reduce((sum, b) => sum + (parseFloat(b.weight_lb) || 0), 0);

  return (
    <>
    <div className="metric-strip mb-6">
      <div className="metric-card">
        <div className="metric-label">Manifiesto ID</div>
        <div className="metric-value" style={{ color: 'var(--blue-text)' }}>{currentManifest?.id || '---'}</div>
        <div className="metric-sub">
          {currentManifest ? (
            <span className="badge badge-done">● ABIERTO</span>
          ) : (
            <span className="badge badge-error">SIN MANIFIESTO</span>
          )}
        </div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Estado de Carga</div>
        <div className="metric-value">{shipCount}</div>
        <div className="metric-sub">PAQUETES LISTOS (SHIP)</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Retenidos (HOLD)</div>
        <div className="metric-value">{holdCount}</div>
        <div className="metric-sub">PARA PRÓXIMO ENVÍO</div>
      </div>
      <div className="metric-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px' }}>
        {currentManifest && (
          <>
            {userPermissions?.canViewCosteo && (
              <button
                onClick={onToggleCosteoPreview}
                className={`btn ${showOpenManifestPreview ? 'btn-primary' : 'btn-secondary'}`}
                style={{ justifyContent: 'center' }}
              >
                {showOpenManifestPreview ? 'Ocultar Preview' : 'Preview Costeo'}
              </button>
            )}
            {userPermissions?.canViewCosteo && (
              <button
                onClick={async () => {
                  const manifestBoxIds = (boxes || [])
                    .filter(b => b.manifest_id === currentManifest.id)
                    .map(b => b.box_id);
                  let recipients = [];
                  if (manifestBoxIds.length > 0) {
                    const { data } = await supabase
                      .from('box_recipients')
                      .select('*')
                      .in('box_id', manifestBoxIds);
                    recipients = data || [];
                  }
                  generateBoxesRutaXLSX(boxes, currentManifest.id, recipients);
                }}
                className="btn btn-secondary"
                style={{ justifyContent: 'center' }}
              >
                📦 Ruta Cajas
              </button>
            )}
            {userPermissions?.canCloseManifest && (
              <button
                onClick={onOpenCloseModal}
                className="btn btn-primary"
                style={{ justifyContent: 'center', background: 'var(--avianca-red)' }}
              >
                <Archive size={14} /> Cerrar Manifiesto
              </button>
            )}
            {userPermissions?.canRequestClose && (
              <button
                onClick={onRequestClose}
                className="btn btn-secondary"
                style={{ justifyContent: 'center', borderColor: 'var(--amber)', color: 'var(--amber)' }}
              >
                Solicitar Cierre
              </button>
            )}
          </>
        )}
      </div>
    </div>
    {currentManifest && totalBoxes > 0 && (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        padding: '8px 16px',
        marginBottom: '16px',
        marginTop: '-12px',
        background: 'var(--blue-soft)',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--blue)',
      }}>
        <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--blue-text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Master Boxes
        </span>
        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>
          📦 {totalBoxes} caja{totalBoxes !== 1 ? 's' : ''}
        </span>
        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>
          ⚖️ {totalBoxLbs.toFixed(1)} lbs
        </span>
      </div>
    )}
    </>
  );
};

// ── Close Manifest Modal ─────────────────────────────────────────────────────
const CloseManifestModal = ({ manifest, shipments, userRole, onCancel, onConfirm }) => {
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [closing, setClosing] = useState(false);

  const manifestShipments = manifest
    ? shipments.filter(s => s.manifest_id === manifest.id)
    : [];
  const shipCount = manifestShipments.filter(s => s.shipping_status === 'Send Immediately').length;
  const holdCount = manifestShipments.filter(s => s.shipping_status === 'Postpone to Next Delivery').length;

  const handleConfirm = async () => {
    setClosing(true);
    setPinError('');
    const result = await onConfirm(userRole === 'admin' ? null : pin);
    setClosing(false);
    if (!result.success) {
      setPinError(result.error || 'Error desconocido.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="font-bold text-gray-900 text-lg mb-1">
          ¿Cerrar Manifiesto {manifest?.id}?
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Esto cerrará <strong className="text-gray-800">{shipCount}</strong> paquetes (SHIP).{' '}
          Los <strong className="text-gray-800">{holdCount}</strong> paquetes en HOLD pasarán
          al próximo manifiesto automáticamente.
        </p>

        {/* PIN input — warehouse only */}
        {userRole !== 'admin' && (
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
              Ingrese el PIN de cierre para confirmar
            </label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={e => { setPin(e.target.value); setPinError(''); }}
              placeholder="• • • •"
              className="w-full text-center text-2xl tracking-[0.5em] font-mono p-3 border border-gray-300 rounded-xl outline-none focus:border-red-400 focus:ring-1 focus:ring-red-300"
            />
          </div>
        )}

        {pinError && (
          <p className="text-red-500 text-xs mb-4 flex items-center gap-1">
            <AlertCircle size={12} /> {pinError}
          </p>
        )}

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={closing}
            className="flex-1 py-2.5 border rounded-xl text-gray-600 text-sm font-bold hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={closing || (userRole !== 'admin' && pin.length === 0)}
            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition flex items-center justify-center gap-1"
          >
            {closing ? 'Cerrando...' : 'Confirmar Cierre'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Shared costeo data builder (file-level — used by both ManifestHistory and LiveManifest) ──
export async function buildCosteoData(manifest) {
    const manifestId = manifest.id || manifest.manifest_id;
    console.log('[Costeo] Building for:', manifestId, 'Keys:', Object.keys(manifest));

    // FETCH 0: Load confirmed declaration draft for item data
    let draftPkgMap = {}; // { [encomienda_package_id]: items[] }
    let draftShipMap = {}; // { [shipment_id]: items[] }
    try {
      const { data: draft } = await supabase
        .from('declaration_drafts')
        .select('payload')
        .eq('manifest_id', manifestId)
        .eq('status', 'confirmed')
        .maybeSingle();
      if (draft?.payload?.packages) {
        for (const dp of draft.payload.packages) {
          if (dp.deck_key?.startsWith('enc-')) {
            const pkgId = dp.deck_key.replace('enc-', '');
            if (dp.items && dp.items.length > 0) {
              draftPkgMap[pkgId] = dp.items;
            }
          } else if (dp.deck_key?.startsWith('ship-')) {
            const shipId = dp.deck_key.replace('ship-', '');
            if (dp.items && dp.items.length > 0) {
              draftShipMap[shipId] = dp.items;
            }
          }
        }
        console.log('[Costeo] Draft items loaded — enc:', Object.keys(draftPkgMap).length, 'ship:', Object.keys(draftShipMap).length);
      }
    } catch (draftErr) {
      console.warn('[Costeo] Could not load draft, falling back to encomienda_items:', draftErr);
    }

    // FETCH 1: Complete encomiendas
    const { data: encomiendas, error: encError } = await supabase
      .from('encomiendas')
      .select('id')
      .eq('manifest_id', manifestId)
      .eq('status', 'COMPLETE');
    if (encError) throw encError;
    console.log('[Costeo] Encomiendas:', (encomiendas || []).length);

    const encIds = (encomiendas || []).map(e => e.id);
    let encomiendaPackages = [];
    let allItems = [];

    if (encIds.length > 0) {
      const { data: pkgs, error: pkgError } = await supabase
        .from('encomienda_packages')
        .select('*')
        .in('encomienda_id', encIds)
        .order('package_number', { ascending: true });
      if (pkgError) throw pkgError;
      encomiendaPackages = pkgs || [];
      console.log('[Costeo] Enc packages:', encomiendaPackages.length);

      const pkgIds = encomiendaPackages.map(p => p.id);
      if (pkgIds.length > 0) {
        const { data: items, error: itemError } = await supabase
          .from('encomienda_items')
          .select('id, package_id, quantity, description, unit_value, condition, customs_catalog ( categoria )')
          .in('package_id', pkgIds);
        if (itemError) throw itemError;
        allItems = items || [];
      }
      console.log('[Costeo] Items:', allItems.length);
    }

    // FETCH 2: Casillero shipments
    const { data: casilleroShipments, error: shipError } = await supabase
      .from('shipments')
      .select('*, customers(full_name)')
      .eq('manifest_id', manifestId)
      .eq('shipping_status', 'Send Immediately')
      .order('entry_timestamp', { ascending: true });
    if (shipError) throw shipError;
    console.log('[Costeo] Casillero shipments:', (casilleroShipments || []).length);

    // Guard: nothing to build
    if (encomiendaPackages.length === 0 && (casilleroShipments || []).length === 0) {
      return null;
    }

    // Fetch box weights from encomienda_boxes (weight_lb lives here, not on encomienda_packages)
    let boxWeightMap = {}; // { [encomienda_id]: total_weight_lb }
    if (encIds.length > 0) {
      const { data: boxWeights } = await supabase
        .from('encomienda_boxes')
        .select('encomienda_id, weight_lb')
        .in('encomienda_id', encIds);
      for (const b of (boxWeights || [])) {
        if (b.weight_lb) {
          boxWeightMap[b.encomienda_id] = (boxWeightMap[b.encomienda_id] || 0) + parseFloat(b.weight_lb);
        }
      }
      console.log('[Costeo] Box weights loaded:', Object.keys(boxWeightMap).length, 'encomiendas with weight');
    }

    // CALCULATE total_lbs from actual package weights
    const encLbs = Object.values(boxWeightMap).reduce((sum, w) => sum + w, 0);
    const casLbs = (casilleroShipments || []).reduce((sum, s) => sum + (parseFloat(s.weight_lb) || 0), 0);
    const total_lbs = parseFloat((encLbs + casLbs).toFixed(2));
    console.log('[Costeo] total_lbs:', total_lbs, '(enc:', encLbs, '+ cas:', casLbs, ')');

    // BUILD encomienda package rows
    const encPackageRows = encomiendaPackages.map(pkg => {
      // Use draft items if available (from DeclarationReview analysis), else fall back to encomienda_items
      const draftItems = draftPkgMap[pkg.id];
      const pkgItems = (draftItems && draftItems.length > 0)
        ? draftItems.map(i => ({
            descripcion:    i.description    || i.descripcion    || '',
            description:    i.description    || i.descripcion    || '',
            cantidad:       Number(i.quantity    ?? i.cantidad       ?? 1),
            quantity:       Number(i.quantity    ?? i.cantidad       ?? 1),
            valor_unitario: Number(i.unit_value  ?? i.valor_unitario ?? i.unit_price ?? 0),
            unit_price:     Number(i.unit_price  ?? i.unit_value     ?? i.valor_unitario ?? 0),
            mercancias:     '',   // draft items have no catalog category — acceptable
            condition:      i.condition || 'NUEVO',
            es_mercancia:   i.is_merchandise !== false,
            is_placeholder: false,
          }))
        : allItems
            .filter(i => i.package_id === pkg.id)
            .map(i => ({
              descripcion:    i.description    || i.descripcion    || '',
              description:    i.description    || i.descripcion    || '',
              cantidad:       Number(i.cantidad    ?? i.quantity       ?? 1),
              quantity:       Number(i.quantity    ?? i.cantidad       ?? 1),
              valor_unitario: Number(i.unit_value  ?? i.valor_unitario ?? i.unit_price ?? i.price ?? 0),
              unit_price:     Number(i.unit_price  ?? i.unit_value     ?? i.valor_unitario ?? i.price ?? 0),
              mercancias:     i.customs_catalog?.categoria || '',
              es_mercancia:   true,
              condition:      i.condition || '',
              is_placeholder: false,
            }));
      return {
        consignatario: pkg.recipient_name || '',
        pkg_number: pkg.unique_package_id || ('PK-' + pkg.package_number),
        peso_lbs: boxWeightMap[pkg.encomienda_id] || 0,
        type: 'ENCOMIENDA',
        items: pkgItems.length > 0 ? pkgItems : [{
          descripcion: '[Sin artículos]',
          cantidad: 1,
          valor_unitario: 0,
          mercancias: '',
          es_mercancia: false,
          is_placeholder: true,
        }],
      };
    });

    // FETCH 3: shipment_items for casillero packages
    const shipmentIds = (casilleroShipments || []).map(s => s.id);
    let shipmentItems = [];
    if (shipmentIds.length > 0) {
      const { data: sItems } = await supabase
        .from('shipment_items')
        .select('*')
        .in('shipment_id', shipmentIds);
      shipmentItems = sItems || [];
    }
    console.log('[Costeo] Shipment items:', shipmentItems.length);

    // BUILD casillero package rows WITH items if available
    const casPackageRows = (casilleroShipments || []).map(s => {
      const draftShipItems = draftShipMap[s.id];
      const pkgItems = (draftShipItems && draftShipItems.length > 0)
        ? draftShipItems.map(i => ({
            descripcion:    i.description    || i.descripcion    || '',
            description:    i.description    || i.descripcion    || '',
            cantidad:       Number(i.quantity    ?? i.cantidad       ?? 1),
            quantity:       Number(i.quantity    ?? i.cantidad       ?? 1),
            valor_unitario: Number(i.unit_value  ?? i.valor_unitario ?? i.unit_price ?? 0),
            unit_price:     Number(i.unit_price  ?? i.unit_value     ?? i.valor_unitario ?? 0),
            mercancias:     '',
            condition:      i.condition || 'NUEVO',
            es_mercancia:   i.is_merchandise !== false,
            is_placeholder: false,
          }))
        : shipmentItems
            .filter(i => i.shipment_id === s.id)
            .map(i => ({
              descripcion:    i.description    || i.descripcion    || '',
              description:    i.description    || i.descripcion    || '',
              cantidad:       Number(i.cantidad    ?? i.quantity       ?? 1),
              quantity:       Number(i.quantity    ?? i.cantidad       ?? 1),
              valor_unitario: Number(i.unit_value  ?? i.valor_unitario ?? i.unit_price ?? i.price ?? 0),
              unit_price:     Number(i.unit_price  ?? i.unit_value     ?? i.valor_unitario ?? i.price ?? 0),
              mercancias:     '',
              es_mercancia:   i.is_merchandise !== false,
              is_placeholder: false,
            }));
      return {
        consignatario: s.recipient_name || s.customers?.full_name || s.customer_id || '',
        pkg_number: s.unique_package_id || '',
        peso_lbs: parseFloat(s.weight_lb) || 0,
        type: 'CASILLERO',
        items: pkgItems.length > 0
          ? pkgItems
          : [{
              descripcion: '[Pendiente análisis]',
              cantidad: null,
              valor_unitario: null,
              mercancias: '',
              es_mercancia: false,
              is_placeholder: true,
            }],
      };
    });

    const allPackages = [...encPackageRows, ...casPackageRows];
    console.log('[Costeo] Total packages:', allPackages.length);

    const costeoData = {
      id: manifestId,
      fecha: manifest.closed_at
        ? new Date(manifest.closed_at).toLocaleDateString('es-SV')
        : new Date().toLocaleDateString('es-SV'),
      total_lbs,
      packages: allPackages,
    };

    console.log('[Costeo] Final costeoData:', costeoData);
    return costeoData;
}

// ── Manifest History ─────────────────────────────────────────────────────────
const ManifestHistory = ({ onReopenManifest, tenant, user, userPermissions }) => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState({});       // { [manifestId]: { shipments, encomiendas, boxes, encBoxes } }
  const [detailLoading, setDetailLoading] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [exportingFotosId, setExportingFotosId] = useState(null);
  const [exportingRecibosId, setExportingRecibosId] = useState(null);
  const [showCosteoPreview, setShowCosteoPreview] = useState({});
  const [costeoPreviewData, setCosteoPreviewData] = useState({});
  const [previewLoading, setPreviewLoading] = useState({});

  // ── Boxful Pendientes state (closed-manifest dispatch) ────────────────────
  const [pendientesOpen, setPendientesOpen] = useState({});         // { [manifestId]: bool }
  const [pendientesStates, setPendientesStates] = useState({});     // { [boxId]: { releaseState, updating } }
  const [pendientesCourierModal, setPendientesCourierModal] = useState(null); // { boxId, couriers, dispatching, error, success }

  const isBoxfulEligibleForPendientes = (b) =>
    tenant?.id === MAXI_ENVIOS_TENANT_ID &&
    (b.delivery_type || '') === 'Drop Off' &&
    !!(b.departamento || '') &&
    (user?.role || '') !== 'client';

  const getPendienteReleaseState = (boxId, fallback) =>
    pendientesStates[boxId]?.releaseState ?? (fallback || 'hold');

  const handlePendientesToggle = async (boxId, newState) => {
    if (pendientesStates[boxId]?.updating) return;
    setPendientesStates(prev => ({
      ...prev,
      [boxId]: { ...prev[boxId], updating: true, releaseState: prev[boxId]?.releaseState ?? newState },
    }));
    try {
      const { error } = await supabase
        .from('boxes')
        .update({ boxful_release_state: newState })
        .eq('box_id', boxId);
      if (error) throw error;
      console.log('[Boxful Hold] box=' + boxId + ' → ' + newState);
      setPendientesStates(prev => ({
        ...prev,
        [boxId]: { releaseState: newState, updating: false },
      }));
      // Update the cached detail so the count badge refreshes
      setDetail(prev => {
        const updated = { ...prev };
        for (const mId of Object.keys(updated)) {
          if (updated[mId]?.boxes) {
            updated[mId] = {
              ...updated[mId],
              boxes: updated[mId].boxes.map(b =>
                b.box_id === boxId ? { ...b, boxful_release_state: newState } : b
              ),
            };
          }
        }
        return updated;
      });
    } catch (err) {
      console.error('[Boxful Hold] update error:', err);
      alert('Error actualizando estado de envío Boxful.');
      setPendientesStates(prev => ({ ...prev, [boxId]: { ...prev[boxId], updating: false } }));
    }
  };

  const handlePendientesDispatch = async (box) => {
    const dept = SV_DEPARTAMENTOS.find(d => d.name === (box.departamento || ''));
    const muni = dept?.municipios?.find(m => m.name === (box.municipio || ''));
    const toCityId = muni?.boxful_city_id;
    if (!toCityId) {
      alert('Este paquete no tiene departamento/municipio válido para despacho Boxful.');
      return;
    }
    setPendientesCourierModal({ boxId: box.box_id, couriers: null, dispatching: false, error: null, success: null });
    const { data, error } = await supabase.functions.invoke('boxful-quoter', { body: { toCityId } });
    if (error || !data?.success) {
      setPendientesCourierModal(prev => prev ? ({ ...prev, couriers: [], error: error?.message || data?.error || 'Error al obtener cotizaciones.' }) : prev);
      return;
    }
    setPendientesCourierModal(prev => prev ? ({ ...prev, couriers: data.couriers }) : prev);
  };

  const handlePendientesSelectCourier = async (courierId) => {
    const boxId = pendientesCourierModal?.boxId;
    if (!boxId || !courierId) return;
    setPendientesCourierModal(prev => prev ? ({ ...prev, dispatching: true, error: null }) : prev);
    const { data, error } = await supabase.functions.invoke('boxful-dispatch', { body: { box_id: boxId, courierId } });
    if (error || !data?.success) {
      setPendientesCourierModal(prev => prev ? ({ ...prev, dispatching: false, error: error?.message || data?.error || 'Error al despachar.' }) : prev);
      return;
    }
    try {
      await supabase.from('boxes').update({ boxful_release_state: 'dispatched' }).eq('box_id', boxId);
      console.log('[Boxful Dispatch] box=' + boxId + ' shipment=' + data.boxful_shipment_id);
      setPendientesStates(prev => ({ ...prev, [boxId]: { releaseState: 'dispatched', updating: false } }));
      setDetail(prev => {
        const updated = { ...prev };
        for (const mId of Object.keys(updated)) {
          if (updated[mId]?.boxes) {
            updated[mId] = {
              ...updated[mId],
              boxes: updated[mId].boxes.map(b =>
                b.box_id === boxId ? { ...b, boxful_release_state: 'dispatched' } : b
              ),
            };
          }
        }
        return updated;
      });
    } catch (releaseErr) {
      console.error('[Boxful Dispatch] release state update error:', releaseErr);
    }
    setPendientesCourierModal(prev => prev ? ({
      ...prev,
      dispatching: false,
      success: `Despachado ✓  ID: ${data.boxful_shipment_id} · Mensajero: ${data.courier}`,
    }) : prev);
  };

  const handleExportCosteo = async (manifest) => {
    setExportingId(manifest.id);
    try {
      const data = await buildCosteoData(manifest);
      if (!data) {
        alert('No hay paquetes ni encomiendas en este manifiesto.');
        return;
      }
      generateCosteoXLSX(data);
      console.log('[Costeo] Export complete');
    } catch (err) {
      console.error('[Costeo] Error:', err);
      alert('Error al obtener datos: ' + err.message);
    } finally {
      setExportingId(null);
    }
  };

  const handleExportFotos = async (m) => {
    setExportingFotosId(m.id);
    try {
      await generateFotosPDF(m.id, supabase);
    } catch (err) {
      console.error('[PDF Fotos] Error:', err);
      alert('Error generando PDF de fotos. Intente de nuevo.');
    } finally {
      setExportingFotosId(null);
    }
  };

  const handleExportRecibos = async (m) => {
    setExportingRecibosId(m.id);
    try {
      await generateRecibosPDF(m.id, supabase);
    } catch (err) {
      console.error('[PDF Recibos] Error:', err);
      alert('Error generando PDF de recibos. Intente de nuevo.');
    } finally {
      setExportingRecibosId(null);
    }
  };

  const handleTogglePreview = async (m) => {
    const nowShowing = !showCosteoPreview[m.id];
    setShowCosteoPreview(prev => ({ ...prev, [m.id]: nowShowing }));
    if (nowShowing && !costeoPreviewData[m.id]) {
      setPreviewLoading(prev => ({ ...prev, [m.id]: true }));
      try {
        const data = await buildCosteoData(m);
        if (data) setCosteoPreviewData(prev => ({ ...prev, [m.id]: data }));
      } catch (e) {
        console.error('[Preview] Error:', e);
      } finally {
        setPreviewLoading(prev => ({ ...prev, [m.id]: false }));
      }
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('manifests')
      .select('*')
      .eq('status', 'CLOSED')
      .order('closed_at', { ascending: false });
    setHistory(data || []);
    setLoading(false);
  };

  const toggleHistory = () => {
    if (!open) fetchHistory();
    setOpen(o => !o);
  };

  const toggleDetail = async (manifestId) => {
    // If already loaded, toggle visibility
    if (detail[manifestId] !== undefined) {
      setDetail(d => { const n = { ...d }; delete n[manifestId]; return n; });
      return;
    }
    setDetailLoading(manifestId);

    const [shipmentsRes, encomiendaRes, boxesRes] = await Promise.all([
      supabase
        .from('shipments')
        .select('*, customers(id, full_name)')
        .eq('manifest_id', manifestId)
        .order('entry_timestamp', { ascending: false }),
      supabase
        .from('encomiendas')
        .select(`
          id, unique_encomienda_id, status, completed_at,
          customers(id, full_name),
          encomienda_packages(
            id, unique_package_id, recipient_name,
            package_number, total_fob, delivery_type,
            photo_url, staff_comments
          )
        `)
        .eq('manifest_id', manifestId)
        .eq('status', 'COMPLETE')
        .order('unique_encomienda_id', { ascending: true }),
      supabase
        .from('boxes')
        .select('*, customers(id, full_name)')
        .eq('manifest_id', manifestId)
        .order('created_at', { ascending: true }),
    ]);

    console.log('[History] encomiendas for', manifestId, ':', encomiendaRes.data, encomiendaRes.error);
    console.log('[History] boxes for', manifestId, ':', boxesRes.data, boxesRes.error);

    // Fetch encomienda_boxes for this manifest's encomiendas
    const encomiendaIds = (encomiendaRes.data || []).map(e => e.id);
    const { data: encBoxesData } = encomiendaIds.length > 0
      ? await supabase
          .from('encomienda_boxes')
          .select('id, box_number, weight_lb, encomienda_id, dimensions')
          .in('encomienda_id', encomiendaIds)
      : { data: [] };
    console.log('[History] encBoxes for', manifestId, ':', (encBoxesData || []).length);

    setDetail(d => ({
      ...d,
      [manifestId]: {
        shipments:   shipmentsRes.data   || [],
        encomiendas: encomiendaRes.data  || [],
        boxes:       boxesRes.data       || [],
        encBoxes:    encBoxesData        || [],
      },
    }));
    setDetailLoading(null);
  };

  return (
    <div className="pt-12 border-t" style={{ borderColor: 'var(--border)' }}>
      <button
        onClick={toggleHistory}
        className="btn btn-secondary w-full"
        style={{ padding: '12px', justifyContent: 'center' }}
      >
        {open ? <ChevronUp size={16} /> : <Archive size={16} />}
        {open ? 'Ocultar Historial de Manifiestos' : 'Ver Historial de Manifiestos (Cerrados)'}
      </button>

      {open && (
        <div className="mt-4 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <p className="text-center text-gray-400 py-8 text-sm">Cargando historial...</p>
          ) : history.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm italic">
              No hay manifiestos cerrados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-bold text-gray-400 uppercase tracking-wide">ID</th>
                    <th className="text-left p-3 font-bold text-gray-400 uppercase tracking-wide">Abierto</th>
                    <th className="text-left p-3 font-bold text-gray-400 uppercase tracking-wide">Cerrado</th>
                    <th className="text-left p-3 font-bold text-gray-400 uppercase tracking-wide">Cerrado por</th>
                    <th className="text-center p-3 font-bold text-gray-400 uppercase tracking-wide">Paquetes</th>
                    <th className="text-center p-3 font-bold text-gray-400 uppercase tracking-wide">HOLD</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.flatMap(m => {
                    const rows = [
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="p-3 font-mono font-bold text-blue-600">{m.id}</td>
                        <td className="p-3 text-gray-500">
                          {m.opened_at ? new Date(m.opened_at).toLocaleDateString() : '---'}
                        </td>
                        <td className="p-3 text-gray-500">
                          {m.closed_at ? new Date(m.closed_at).toLocaleString() : '---'}
                        </td>
                        <td className="p-3 text-gray-600 font-medium">{m.closed_by || '---'}</td>
                        <td className="p-3 text-center font-bold text-green-700">
                          {m.total_packages ?? '---'}
                        </td>
                        <td className="p-3 text-center font-bold text-gray-500">
                          {m.total_hold ?? '---'}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleDetail(m.id)}
                              className="text-blue-600 font-bold hover:underline flex items-center gap-1 whitespace-nowrap"
                            >
                              {detailLoading === m.id
                                ? '...'
                                : detail[m.id]
                                ? 'Ocultar'
                                : 'Ver detalles'}
                              {!detail[m.id] && detailLoading !== m.id && (
                                <ChevronRight size={11} />
                              )}
                            </button>
                            <button
                              onClick={() => handleExportCosteo(m)}
                              disabled={exportingId === m.id}
                              className="text-emerald-700 font-bold hover:underline flex items-center gap-1 whitespace-nowrap disabled:opacity-50"
                            >
                              {exportingId === m.id ? '...' : '📊 Costeo'}
                            </button>
                            <button
                              onClick={() => handleExportFotos(m)}
                              disabled={exportingFotosId === m.id}
                              className="text-blue-600 font-bold hover:underline flex items-center gap-1 whitespace-nowrap disabled:opacity-50"
                            >
                              {exportingFotosId === m.id ? '...' : '📷 Fotos'}
                            </button>
                            <button
                              onClick={() => handleExportRecibos(m)}
                              disabled={exportingRecibosId === m.id}
                              className="text-amber-600 font-bold hover:underline flex items-center gap-1 whitespace-nowrap disabled:opacity-50"
                            >
                              {exportingRecibosId === m.id ? '...' : '🧾 Recibos'}
                            </button>
                            <button
                              onClick={() => handleTogglePreview(m)}
                              disabled={previewLoading[m.id]}
                              style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                padding: '4px 12px',
                                border: '1px solid #1D9E75',
                                borderRadius: '16px',
                                background: showCosteoPreview[m.id] ? '#1D9E75' : 'transparent',
                                color: showCosteoPreview[m.id] ? '#fff' : '#1D9E75',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                opacity: previewLoading[m.id] ? 0.5 : 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {previewLoading[m.id] ? '...' : showCosteoPreview[m.id] ? 'Ocultar Preview' : 'Preview Costeo'}
                            </button>
                            <button
                              onClick={() => onReopenManifest(m)}
                              style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                padding: '4px 12px',
                                border: '1px solid var(--avianca-red)',
                                borderRadius: '16px',
                                background: 'transparent',
                                color: 'var(--avianca-red)',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Abrir Manifiesto
                            </button>
                          </div>
                        </td>
                      </tr>,
                    ];

                    if (detail[m.id]) {
                      const d = detail[m.id];
                      const hasAnything = d.shipments.length + d.encomiendas.length + d.boxes.length > 0;
                      rows.push(
                        <tr key={m.id + '-detail'}>
                          <td colSpan={7} className="bg-slate-50 px-4 py-4 border-t border-slate-100">
                            {!hasAnything ? (
                              <p className="text-gray-400 text-xs italic">
                                Sin paquetes en este manifiesto.
                              </p>
                            ) : (
                              <div className="max-h-96 overflow-y-auto pr-1 space-y-4">

                                {/* ── Casillero shipments ── */}
                                {d.shipments.length > 0 && (
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                                      Casillero ({d.shipments.length})
                                    </p>
                                    <div className="space-y-1">
                                      {d.shipments.map(s => (
                                        <div
                                          key={s.id}
                                          className="flex items-center gap-3 text-xs py-1.5 border-b border-slate-100 last:border-0"
                                        >
                                          <span className="font-mono font-bold text-blue-600 w-24 shrink-0">
                                            {s.unique_package_id}
                                          </span>
                                          <span className="text-gray-500 w-32 shrink-0 truncate">
                                            {s.customers?.full_name || s.customer_id}
                                          </span>
                                          <span
                                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase shrink-0 ${
                                              s.shipment_type === 'ENCOMIENDA'
                                                ? 'bg-orange-100 text-orange-700 border-orange-200'
                                                : 'bg-blue-100 text-blue-700 border-blue-100'
                                            }`}
                                          >
                                            {s.shipment_type || 'CASILLERO'}
                                          </span>
                                          <span
                                            className={`text-[9px] font-bold shrink-0 ${
                                              s.shipping_status === 'Send Immediately'
                                                ? 'text-green-600'
                                                : 'text-gray-400'
                                            }`}
                                          >
                                            {s.shipping_status === 'Send Immediately' ? 'SHIP' : 'HOLD'}
                                          </span>
                                          {s.tracking_number && (
                                            <span className="text-gray-400 font-mono truncate">
                                              {s.tracking_number}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* ── Encomiendas ── */}
                                {d.encomiendas.length > 0 && (
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                                      Encomiendas ({d.encomiendas.length})
                                    </p>
                                    <div className="space-y-2">
                                      {d.encomiendas.map(enc => (
                                        <div key={enc.id} className="border border-orange-100 rounded-lg p-2.5 bg-orange-50/40">
                                          <div className="flex items-center justify-between mb-1.5">
                                            <span className="font-mono font-bold text-orange-700 text-xs">
                                              {enc.unique_encomienda_id}
                                            </span>
                                            <div className="flex gap-1.5">
                                              <span className="text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold">
                                                ENCOMIENDA
                                              </span>
                                              <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">
                                                COMPLETADO
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-[11px] text-gray-500 mb-1.5">
                                            {enc.customers?.full_name} · {enc.encomienda_packages?.length || 0} paquete(s)
                                          </p>
                                          <div className="space-y-1">
                                            {(enc.encomienda_packages || []).map(pkg => (
                                              <div key={pkg.id} className="flex items-center justify-between text-[11px] text-gray-500 pl-2 border-l-2 border-orange-200">
                                                <span>
                                                  <span className="font-mono text-orange-600 font-bold">
                                                    {pkg.unique_package_id || ('PK-' + pkg.package_number)}
                                                  </span>
                                                  {' → '}{pkg.recipient_name}
                                                  {pkg.delivery_type ? ' · ' + pkg.delivery_type : ''}
                                                </span>
                                                {pkg.total_fob > 0 && (
                                                  <span className="text-green-700 font-bold shrink-0 ml-2">
                                                    ${parseFloat(pkg.total_fob).toFixed(2)} FOB
                                                  </span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* ── Master Boxes (casillero + encomienda) ── */}
                                {(() => {
                                  const allBoxes = [
                                    ...(d.boxes || []).map(b => ({
                                      key: b.box_id || b.id,
                                      label: b.box_id,
                                      sublabel: b.customers?.full_name,
                                      weight_lb: b.weight_lb || 0,
                                      dimensions: b.dimensions,
                                      type: 'casillero',
                                    })),
                                    ...(d.encBoxes || []).map(b => ({
                                      key: b.id,
                                      label: `ENC Caja ${b.box_number || b.id.slice(0, 4)}`,
                                      sublabel: null,
                                      weight_lb: b.weight_lb || 0,
                                      dimensions: b.dimensions,
                                      type: 'encomienda',
                                    })),
                                  ];
                                  const totalLbs = allBoxes.reduce((s, b) => s + (b.weight_lb || 0), 0);
                                  if (allBoxes.length === 0) return null;
                                  return (
                                    <div>
                                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                                        Master Boxes ({allBoxes.length})
                                      </p>
                                      <div className="space-y-1">
                                        {allBoxes.map(box => (
                                          <div key={box.key} className="flex items-center justify-between text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white">
                                            <div className="flex items-center gap-2">
                                              {box.type === 'encomienda' && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-700">ENC</span>
                                              )}
                                              <span className="font-mono font-bold text-gray-700">{box.label}</span>
                                              {box.sublabel && <span className="text-gray-400">{box.sublabel}</span>}
                                            </div>
                                            <div className="flex items-center gap-3 text-gray-400 text-[11px]">
                                              {box.weight_lb > 0 && <span>{box.weight_lb} lbs</span>}
                                              {box.dimensions && <span>{box.dimensions}</span>}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-[11px] text-gray-400 mt-1.5">
                                        Total: {allBoxes.length} caja{allBoxes.length !== 1 ? 's' : ''} · {totalLbs.toFixed(1)} lbs
                                      </p>
                                    </div>
                                  );
                                })()}

                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    // Preview row — independent of detail expansion
                    if (showCosteoPreview[m.id]) {
                      rows.push(
                        <tr key={m.id + '-preview'}>
                          <td colSpan={7} style={{ padding: '0 16px 16px', background: '#f8fafc', borderTop: '0.5px solid #e2e8f0' }}>
                            {previewLoading[m.id] ? (
                              <p style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: '#6b7280' }}>
                                Cargando costeo...
                              </p>
                            ) : costeoPreviewData[m.id] ? (
                              <div>
                                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', paddingTop: '14px', marginBottom: '4px' }}>
                                  Vista previa del costeo — {m.id}
                                </div>
                                <CosteoPreview
                                  manifest={costeoPreviewData[m.id]}
                                  packages={costeoPreviewData[m.id].packages}
                                  onExport={() => generateCosteoXLSX(costeoPreviewData[m.id])}
                                />
                              </div>
                            ) : (
                              <p style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                                Sin datos disponibles.
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    // ── Boxful Pendientes section (MaxiEnvios only) ───────────
                    if (
                      tenant?.id === MAXI_ENVIOS_TENANT_ID &&
                      (user?.role || '') !== 'client' &&
                      detail[m.id]
                    ) {
                      const pendientesBoxes = (detail[m.id].boxes || []).filter(b =>
                        isBoxfulEligibleForPendientes(b) &&
                        getPendienteReleaseState(b.box_id, b.boxful_release_state) !== 'dispatched' &&
                        !b.boxful_shipment_id
                      );
                      const isOpen = !!pendientesOpen[m.id];
                      rows.push(
                        <tr key={m.id + '-pendientes'}>
                          <td colSpan={7} style={{ padding: '0 16px 8px', background: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
                            <div style={{ paddingTop: '8px' }}>
                              <button
                                type="button"
                                onClick={() => setPendientesOpen(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  fontSize: '12px', fontWeight: '700', color: '#15803d',
                                  padding: '4px 0',
                                }}
                              >
                                📦 Boxful Pendientes
                                <span style={{
                                  background: '#dcfce7', color: '#15803d', borderRadius: '99px',
                                  padding: '1px 8px', fontSize: '11px', fontWeight: '700',
                                }}>
                                  {pendientesBoxes.length}
                                </span>
                                {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              </button>

                              {isOpen && pendientesBoxes.length === 0 && (
                                <p style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', padding: '8px 0' }}>
                                  Sin cajas pendientes de despacho.
                                </p>
                              )}

                              {isOpen && pendientesBoxes.map(b => {
                                const rs = getPendienteReleaseState(b.box_id, b.boxful_release_state);
                                const isUpdating = !!pendientesStates[b.box_id]?.updating;
                                return (
                                  <div key={b.box_id} style={{
                                    marginTop: '8px', padding: '10px 12px',
                                    background: '#fff', border: '1px solid #bbf7d0',
                                    borderRadius: '8px', fontSize: '12px',
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                                      <div>
                                        <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#1d4ed8' }}>{b.box_id}</span>
                                        {b.recipient_name && <span style={{ marginLeft: '8px', color: '#374151' }}>{b.recipient_name}</span>}
                                        {b.recipient_phone && <span style={{ marginLeft: '8px', color: '#6b7280' }}><span>📞</span> {b.recipient_phone || ''}</span>}
                                        <div style={{ color: '#6b7280', marginTop: '2px' }}>
                                          {(b.departamento || '') && (b.municipio || '') ? `${b.departamento}, ${b.municipio}` : (b.departamento || '')}
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                        {/* Hold/Send toggle */}
                                        <div style={{
                                          display: 'flex', border: '1px solid #d1d5db', borderRadius: '6px',
                                          overflow: 'hidden', opacity: isUpdating ? 0.6 : 1,
                                        }}>
                                          <button
                                            type="button"
                                            disabled={isUpdating}
                                            onClick={() => rs !== 'hold' && handlePendientesToggle(b.box_id, 'hold')}
                                            style={{
                                              padding: '3px 10px', fontSize: '11px', fontWeight: '600',
                                              background: rs === 'hold' ? '#6b7280' : 'transparent',
                                              color: rs === 'hold' ? '#fff' : '#6b7280',
                                              border: 'none', cursor: rs === 'hold' ? 'default' : 'pointer',
                                            }}
                                          >Hold</button>
                                          <button
                                            type="button"
                                            disabled={isUpdating}
                                            onClick={() => rs !== 'released' && handlePendientesToggle(b.box_id, 'released')}
                                            style={{
                                              padding: '3px 10px', fontSize: '11px', fontWeight: '600',
                                              background: rs === 'released' ? '#00D4B8' : 'transparent',
                                              color: rs === 'released' ? '#fff' : '#00D4B8',
                                              border: 'none', borderLeft: '1px solid #d1d5db',
                                              cursor: rs === 'released' ? 'default' : 'pointer',
                                            }}
                                          >Send</button>
                                        </div>
                                        {rs === 'released' && (
                                          <button
                                            type="button"
                                            onClick={() => handlePendientesDispatch(b)}
                                            style={{
                                              padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                                              background: '#00D4B8', color: '#fff',
                                              border: 'none', borderRadius: '5px', cursor: 'pointer',
                                              display: 'flex', alignItems: 'center', gap: '4px',
                                            }}
                                          >
                                            <Truck size={11} /> Confirmar envío
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return rows;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pendientes courier modal */}
      {pendientesCourierModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '360px', margin: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontWeight: '700', fontSize: '15px' }}>Seleccionar Mensajero</span>
              <button onClick={() => setPendientesCourierModal(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            {pendientesCourierModal.success ? (
              <p style={{ color: '#16a34a', fontWeight: '500', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>{pendientesCourierModal.success}</p>
            ) : (
              <>
                {pendientesCourierModal.error && (
                  <p style={{ color: '#dc2626', fontSize: '12px', marginBottom: '12px', padding: '8px', background: '#fef2f2', borderRadius: '6px' }}>{pendientesCourierModal.error}</p>
                )}
                {pendientesCourierModal.couriers === null ? (
                  <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', padding: '24px 0' }}>Obteniendo cotizaciones...</p>
                ) : pendientesCourierModal.couriers.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', padding: '24px 0' }}>Sin mensajeros disponibles para esta ruta.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {pendientesCourierModal.couriers.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        disabled={pendientesCourierModal.dispatching}
                        onClick={() => handlePendientesSelectCourier(c.id)}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px',
                          background: 'transparent', cursor: 'pointer', fontSize: '13px',
                          opacity: pendientesCourierModal.dispatching ? 0.5 : 1,
                        }}
                      >
                        <span style={{ fontWeight: '500' }}>{c.name}</span>
                        <span style={{ fontWeight: '700', color: '#15803d' }}>${Number(c.clientPrice).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {pendientesCourierModal.dispatching && (
                  <p style={{ color: '#3b82f6', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', marginTop: '12px' }}>Despachando...</p>
                )}
              </>
            )}
            <button
              type="button"
              onClick={() => setPendientesCourierModal(null)}
              className="btn btn-ghost w-full"
              style={{ marginTop: '16px' }}
            >
              {pendientesCourierModal.success ? 'Cerrar' : 'Cancelar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Box Card with Recipients ──────────────────────────────────────────────────
const BoxCard = ({ box, user, onEdit, onDelete, onDispatch, onUpdateBox }) => {
  const { tenant } = useTenant();
  const [expanded, setExpanded] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', address: '', deliveryType: 'Pick Up' });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [updatingState, setUpdatingState] = useState(false);

  // Defensive read — boxful_release_state may be null on cached rows; treat null as 'hold'
  const releaseState = box.boxful_release_state || 'hold';

  // TODO: add PERMISSIONS.dispatchBoxful key to App.js in a follow-up
  const isBoxfulEligible =
    tenant?.id === MAXI_ENVIOS_TENANT_ID &&
    (box.delivery_type || '') === 'Drop Off' &&
    !!(box.departamento || '') &&
    user?.role !== 'client';

  const handleToggleReleaseState = async (newState) => {
    if (updatingState) return;
    setUpdatingState(true);
    try {
      const { error } = await supabase
        .from('boxes')
        .update({ boxful_release_state: newState })
        .eq('box_id', box.box_id);
      if (error) throw error;
      console.log('[Boxful Hold] box=' + box.box_id + ' → ' + newState);
      if (onUpdateBox) onUpdateBox(box.box_id, { boxful_release_state: newState });
    } catch (err) {
      console.error('[Boxful Hold] update error:', err);
      alert('Error actualizando estado de envío Boxful.');
    } finally {
      setUpdatingState(false);
    }
  };

  const fetchRecipients = async () => {
    setLoadingRecipients(true);
    const { data } = await supabase
      .from('box_recipients')
      .select('*')
      .eq('box_id', box.box_id)
      .order('created_at', { ascending: true });
    setRecipients(data || []);
    setLoadingRecipients(false);
  };

  const toggleExpand = () => {
    if (!expanded) fetchRecipients();
    setExpanded(e => !e);
  };

  const handleAddRecipient = async () => {
    if (!addForm.name.trim()) { setAddError('El nombre es obligatorio.'); return; }
    setAdding(true);
    setAddError('');
    const { error } = await supabase.from('box_recipients').insert({
      box_id: box.box_id,
      recipient_name: addForm.name.trim(),
      recipient_phone: addForm.phone.replace(/\D/g, '') || null,
      recipient_address: addForm.address.trim() || null,
      delivery_type: addForm.deliveryType,
    });
    setAdding(false);
    if (error) {
      setAddError(error.message);
    } else {
      setAddSuccess('Destinatario agregado ✓');
      setAddForm({ name: '', phone: '', address: '', deliveryType: 'Pick Up' });
      fetchRecipients();
      setTimeout(() => setAddSuccess(''), 3000);
    }
  };

  const handleDeleteRecipient = async (recipient) => {
    if (!window.confirm('¿Eliminar este destinatario?')) return;
    await supabase.from('box_recipients').delete().eq('id', recipient.id);
    fetchRecipients();
  };

  const isAdmin = user?.role === 'admin';
  const canEditDelete = ['admin', 'manager', 'bodega'].includes(user?.role);

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="p-3 flex justify-between items-center">
        <div className="flex gap-3 items-center">
          <BoxIcon size={16} className="text-blue-500"/>
          <span className="font-mono font-bold text-blue-600">{box.box_id}</span>
          <span className="text-xs text-gray-500">{box.weight_lb} lb | {box.dimensions}</span>
        </div>
        <div className="flex gap-1 items-center">
          <button
            onClick={toggleExpand}
            className="text-xs text-blue-600 font-bold px-2 py-1 rounded hover:bg-blue-50 flex items-center gap-1 transition"
          >
            Destinatarios {expanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          </button>
          {box.boxful_shipment_id || releaseState === 'dispatched' ? (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-200 bg-green-50 text-green-700">✓ Boxful</span>
          ) : !isBoxfulEligible && ((box.delivery_type || '') === 'Drop Off' && (box.departamento || '') && onDispatch) ? (
            <button
              onClick={onDispatch}
              className="text-xs text-green-600 font-bold px-2 py-1 rounded hover:bg-green-50 flex items-center gap-1 transition"
              title="Despachar con Boxful"
            >
              <Truck size={12}/> Boxful
            </button>
          ) : null}
          {canEditDelete && (
            <button onClick={onEdit} className="text-blue-400 hover:text-blue-600 p-1"><Edit size={14}/></button>
          )}
          {canEditDelete && (
            <button onClick={onDelete} className="text-red-300 hover:text-red-500 p-1"><Trash2 size={14}/></button>
          )}
        </div>
      </div>

      {/* Boxful Hold/Send toggle — MaxiEnvios Drop-Off boxes with departamento, not yet dispatched */}
      {isBoxfulEligible && releaseState !== 'dispatched' && !box.boxful_shipment_id && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 12px', background: '#fafafa' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>Boxful:</span>
            <div style={{
              display: 'flex', border: '1px solid #d1d5db', borderRadius: '6px',
              overflow: 'hidden', opacity: updatingState ? 0.6 : 1,
            }}>
              <button
                type="button"
                disabled={updatingState}
                onClick={() => releaseState !== 'hold' && handleToggleReleaseState('hold')}
                style={{
                  padding: '3px 10px', fontSize: '11px', fontWeight: '600',
                  background: releaseState === 'hold' ? '#6b7280' : 'transparent',
                  color: releaseState === 'hold' ? '#fff' : '#6b7280',
                  border: 'none', cursor: releaseState === 'hold' ? 'default' : 'pointer',
                }}
              >Hold</button>
              <button
                type="button"
                disabled={updatingState}
                onClick={() => releaseState !== 'released' && handleToggleReleaseState('released')}
                style={{
                  padding: '3px 10px', fontSize: '11px', fontWeight: '600',
                  background: releaseState === 'released' ? '#00D4B8' : 'transparent',
                  color: releaseState === 'released' ? '#fff' : '#00D4B8',
                  border: 'none', borderLeft: '1px solid #d1d5db',
                  cursor: releaseState === 'released' ? 'default' : 'pointer',
                }}
              >Send</button>
            </div>
          </div>
          {releaseState === 'released' && (
            <button
              type="button"
              onClick={onDispatch}
              disabled={!onDispatch}
              style={{
                marginTop: '6px', width: '100%', padding: '6px 12px',
                fontSize: '12px', fontWeight: '600', background: '#00D4B8',
                color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
            >
              <Truck size={12}/> Confirmar envío a Boxful
            </button>
          )}
        </div>
      )}

      {expanded && (
        <div className="border-t bg-slate-50/60 p-3 space-y-3">
          {/* Recipients list */}
          {loadingRecipients ? (
            <p className="text-xs text-gray-400 italic">Cargando...</p>
          ) : recipients.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Sin destinatarios registrados.</p>
          ) : (
            <div className="space-y-1.5">
              {recipients.map(r => (
                <div key={r.id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-100">
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <span className="font-medium text-sm text-gray-800 shrink-0">{r.recipient_name}</span>
                    {r.recipient_phone && (
                      <span className="text-xs text-gray-500 flex items-center gap-1 shrink-0">
                        <Phone size={10}/> {r.recipient_phone}
                      </span>
                    )}
                    {r.recipient_address && (
                      <span className="text-xs text-gray-500 flex items-center gap-1 truncate">
                        <MapPin size={10}/> {r.recipient_address}
                      </span>
                    )}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${
                      r.delivery_type === 'Drop Off'
                        ? 'bg-orange-100 text-orange-700 border-orange-200'
                        : 'bg-green-100 text-green-700 border-green-200'
                    }`}>
                      {(r.delivery_type || 'PICK UP').toUpperCase()}
                    </span>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteRecipient(r)}
                      className="text-red-300 hover:text-red-500 shrink-0 ml-2 p-1"
                    >
                      <Trash2 size={12}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add recipient form */}
          <div className="pt-2 border-t border-gray-200 space-y-2">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Agregar Destinatario</p>
            <input
              className="w-full p-2 border rounded-lg text-sm bg-white outline-none focus:ring-1 focus:ring-blue-400"
              placeholder="Nombre del destinatario *"
              value={addForm.name}
              onChange={e => setAddForm({...addForm, name: e.target.value})}
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                className="p-2 border rounded-lg text-sm bg-white outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="Teléfono"
                value={addForm.phone}
                onChange={e => setAddForm({...addForm, phone: e.target.value})}
              />
              <input
                className="p-2 border rounded-lg text-sm bg-white outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="Dirección"
                value={addForm.address}
                onChange={e => setAddForm({...addForm, address: e.target.value})}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAddForm({...addForm, deliveryType: 'Pick Up'})}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition ${addForm.deliveryType === 'Pick Up' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                PICK UP
              </button>
              <button
                type="button"
                onClick={() => setAddForm({...addForm, deliveryType: 'Drop Off'})}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition ${addForm.deliveryType === 'Drop Off' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                DROP OFF
              </button>
            </div>
            {addError && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={11}/> {addError}
              </p>
            )}
            {addSuccess && <p className="text-xs text-green-600 font-medium">{addSuccess}</p>}
            <button
              type="button"
              onClick={handleAddRecipient}
              disabled={adding}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition"
            >
              <PlusCircle size={12}/> {adding ? 'Agregando...' : 'Agregar Destinatario'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Encomienda Manifest Row ───────────────────────────────────────────────────
const EncomiendaReceiptCell = ({ pkg, encPublicId, manifestId, onUploaded }) => {
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const { tenant } = useTenant();

  const fetchReceipts = async () => {
    const { data } = await supabase
      .from('package_receipts')
      .select('*')
      .eq('encomienda_package_id', pkg.id)
      .order('uploaded_at', { ascending: true });

    // Merge legacy single receipt_url if it exists and isn't already in package_receipts
    const merged = data || [];
    if (pkg.receipt_url && !merged.some(r => r.receipt_url === pkg.receipt_url)) {
      merged.unshift({
        id: 'legacy-' + pkg.id,
        receipt_url: pkg.receipt_url,
        source: 'staff',
        uploaded_at: null,
        _legacy: true,
      });
    }
    setReceipts(merged);
  };

  /* eslint-disable */
  useEffect(() => { if (!tenant) return; fetchReceipts(); }, [tenant, pkg.id, pkg.receipt_url]);
  /* eslint-enable */

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 20 - (receipts || []).length;
    if (remaining <= 0) { setErr('Maximo 20 recibos.'); e.target.value = ''; return; }
    const toUpload = files.slice(0, remaining);
    setErr('');
    setUploading(true);
    let lastUrl = null;
    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) { setErr('Maximo 5MB por archivo.'); continue; }
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const filePath = `receipts/${manifestId || 'enc'}/${encPublicId}-PK${pkg.package_number}-${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage.from('package-photos').upload(filePath, file);
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(filePath);
        await supabase.from('package_receipts').insert({
          encomienda_package_id: pkg.id,
          receipt_url: urlData.publicUrl,
          uploaded_by: 'staff',
          source: 'staff',
          manifest_id: manifestId || null,
        });
        lastUrl = urlData.publicUrl;
      } catch (uploadErr) {
        setErr(uploadErr.message);
      }
    }
    if (lastUrl) {
      try {
        await supabase.from('encomienda_packages').update({ receipt_url: lastUrl }).eq('id', pkg.id);
      } catch (legacyErr) {
        console.warn('[EncReceiptCell] legacy update failed (non-blocking):', legacyErr);
      }
    }
    await fetchReceipts();
    setUploading(false);
    e.target.value = '';
    if (onUploaded) onUploaded();
  };

  const handleDelete = async (receipt) => {
    if (!receipt) return;
    if (!window.confirm('Eliminar este recibo?')) return;
    if (receipt._legacy) {
      // Legacy receipt lives in encomienda_packages.receipt_url — null it out
      try {
        await supabase
          .from('encomienda_packages')
          .update({ receipt_url: null })
          .eq('id', pkg.id);
      } catch (legacyErr) {
        console.warn('[EncReceiptCell] legacy delete failed:', legacyErr);
      }
    } else {
      await supabase.from('package_receipts').delete().eq('id', receipt.id);
    }
    await fetchReceipts();
  };

  const count = (receipts || []).length;

  return (
    <div style={{ marginTop: '8px', width: '100%' }}>
      <div style={{
        fontSize: '11px', fontWeight: '500', color: 'var(--text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>Recibos ({count}/20)</span>
        {count < 20 && (
          <label style={{
            fontSize: '11px', fontWeight: '500', padding: '4px 10px',
            border: '1px solid var(--blue)', borderRadius: '8px',
            color: 'var(--blue-text)', cursor: 'pointer', background: 'var(--blue-soft)',
          }}>
            {uploading ? 'Subiendo...' : '+ Agregar'}
            <input type="file" multiple accept="image/jpeg,image/png,application/pdf"
              onChange={handleFile} disabled={uploading} style={{ display: 'none' }} />
          </label>
        )}
      </div>
      {err && <p style={{ fontSize: '11px', color: 'var(--avianca-red)', marginBottom: '6px' }}>{err}</p>}
      {count === 0 ? (
        <label style={{
          display: 'block', border: '1px dashed var(--border)', borderRadius: '8px',
          padding: '16px', textAlign: 'center', fontSize: '12px',
          color: 'var(--text-secondary)', fontStyle: 'italic', cursor: 'pointer',
        }}>
          {uploading ? 'Subiendo...' : 'Subir recibo'}
          <input type="file" multiple accept="image/jpeg,image/png,application/pdf"
            onChange={handleFile} disabled={uploading} style={{ display: 'none' }} />
        </label>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '6px' }}>
          {receipts.map(r => (
            <div key={r.id} style={{
              position: 'relative', border: '1px solid var(--border)',
              borderRadius: '6px', overflow: 'hidden', aspectRatio: '1',
              background: 'var(--bg)',
            }}>
              <a href={r.receipt_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', height: '100%', fontSize: '10px', textDecoration: 'none',
                  color: 'var(--text-secondary)' }}>
                {r.receipt_url && r.receipt_url.toLowerCase().includes('.pdf')
                  ? 'PDF'
                  : <img src={r.receipt_url} alt="receipt"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </a>
              <button onClick={() => handleDelete(r)} style={{
                position: 'absolute', top: '2px', right: '2px',
                background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '4px',
                color: '#fff', fontSize: '10px', cursor: 'pointer', padding: '1px 4px', lineHeight: '1.4',
              }}>x</button>
              <div style={{
                position: 'absolute', bottom: '2px', left: '2px', fontSize: '8px',
                color: '#fff', background: 'rgba(0,0,0,0.4)', borderRadius: '3px', padding: '1px 3px',
              }}>
                {r._legacy ? 'Legacy' : (r.source === 'client' ? 'Cliente' : 'Staff')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EncomiendaPhotoCell = ({ pkg, encPublicId }) => {
  const [uploading, setUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState(pkg.photo_url || null);
  const [err, setErr] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Máximo 5MB'); return; }
    setErr('');
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${encPublicId}-PK${pkg.package_number}-photo-${Date.now()}.${ext}`;
      await supabase.storage.from('package-photos').upload(fileName, file);
      const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(fileName);
      await supabase.from('encomienda_packages').update({ photo_url: urlData.publicUrl }).eq('id', pkg.id);
      setLocalUrl(urlData.publicUrl);
    } catch (error) {
      setErr(error.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="shrink-0">
      {localUrl ? (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="flex items-center gap-1 text-blue-600 text-[10px] font-bold">
            <CheckCircle size={10} /> Foto
          </span>
          <a href={localUrl} target="_blank" rel="noreferrer"
            className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
          >
            <ExternalLink size={9} /> Ver
          </a>
          <label className="text-[10px] text-blue-400 hover:text-blue-600 cursor-pointer font-medium">
            Cambiar
            <input type="file" className="hidden"
              accept="image/jpeg,image/png"
              onChange={handleFile} />
          </label>
        </div>
      ) : (
        <label className={`cursor-pointer inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border transition ${
          uploading ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'
        }`}>
          {uploading ? 'Subiendo...' : '📷 Foto'}
          <input type="file" className="hidden" accept="image/jpeg,image/png" onChange={handleFile} disabled={uploading} />
        </label>
      )}
      {err && <p className="text-red-500 text-[9px] mt-0.5">{err}</p>}
    </div>
  );
};

const EncomiendaManifestRow = ({ enc, manifestId, onEdit, onDelete, onReceiptUploaded }) => {
  const [expanded, setExpanded] = useState(false);
  const [encBoxes, setEncBoxes] = useState(null); // null = not loaded yet
  const pkgs = enc.encomienda_packages || [];
  const missingReceipts = pkgs.filter(p => !p.receipt_url).length;

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && encBoxes === null) {
      const { data } = await supabase
        .from('encomienda_boxes')
        .select('*')
        .eq('encomienda_id', enc.id)
        .order('box_number', { ascending: true });
      setEncBoxes(data || []);
    }
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    const phone = (enc.customers?.phone_number || '').replace(/\D/g, '');
    if (!phone) { alert('Este cliente no tiene número de teléfono registrado.'); return; }
    const trackingUrl = enc.tracking_token
      ? `${window.location.origin}/track/${enc.tracking_token}`
      : null;
    const lines = [
      `📦 *Encomienda ${enc.unique_encomienda_id}*`,
      `Hola ${enc.customers?.full_name || 'cliente'}, su encomienda ha sido procesada.`,
      ``,
      `*Destinatarios:*`,
      ...pkgs.map(p => `• ${p.unique_package_id || `PK-${p.package_number}`}: ${p.recipient_name} (${p.delivery_type || 'Drop Off'})`),
    ];
    if (trackingUrl) lines.push(``, `🔍 Seguimiento: ${trackingUrl}`);
    const msg = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  return (
    <div className={`transition ${expanded ? 'bg-orange-50/30' : ''}`}>
      <div
        className="grid grid-cols-12 gap-2 items-center p-4 cursor-pointer hover:bg-gray-50"
        onClick={handleExpand}
      >
        <div className="col-span-1 flex justify-center text-gray-400">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        <div className="col-span-2 font-mono font-bold text-orange-600 text-sm">
          {enc.unique_encomienda_id}
        </div>
        <div className="col-span-2">
          <span className="text-xs font-bold uppercase text-orange-600 bg-orange-100 px-2 py-0.5 rounded border border-orange-200">
            ENCOMIENDA
          </span>
        </div>
        <div className="col-span-2 flex items-center gap-1 text-[10px] text-gray-500 font-medium">
          <Clock size={10} /> {enc.created_at ? new Date(enc.created_at).toLocaleString() : '---'}
        </div>
        <div className="col-span-3 text-xs font-medium text-gray-400 truncate">
          {enc.customers?.full_name || enc.customer_id}
          {pkgs.length > 0 && ` · ${pkgs.length} ${pkgs.length === 1 ? 'dest.' : 'dests.'}`}
          {missingReceipts > 0 && (
            <span className="ml-1 text-orange-500 font-bold">· {missingReceipts} recibo(s) ⚠️</span>
          )}
        </div>
        <div className="col-span-2 flex items-center justify-end gap-1.5">
          <span className="px-2 py-1 rounded text-[10px] font-bold uppercase border bg-green-100 text-green-700 border-green-200">
            COMPLETADO
          </span>
          <button
            onClick={handleWhatsApp}
            className="p-1 text-green-500 hover:text-green-700 transition"
            title="Enviar WhatsApp"
          >
            <MessageCircle size={14} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onEdit(enc.id); }}
            className="p-1 text-orange-400 hover:text-orange-600 transition"
            title="Editar encomienda"
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(enc.id); }}
            className="p-1 text-red-400 hover:text-red-600 transition"
            title="Eliminar encomienda"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 bg-white border-t border-orange-100 space-y-2">
          {pkgs.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Sin paquetes registrados.</p>
          ) : (
            pkgs.map(pkg => (
              <React.Fragment key={pkg.id}>
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100 text-xs flex-wrap">
                  <span className="font-bold text-orange-600 shrink-0">{pkg.unique_package_id || `PK-${pkg.package_number}`}</span>
                  <span className="font-medium text-gray-800 flex-1 min-w-0 truncate">{pkg.recipient_name}</span>
                  <span className={`px-2 py-0.5 rounded-full font-bold border shrink-0 ${
                    pkg.delivery_type === 'Pick Up'
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : 'bg-orange-100 text-orange-700 border-orange-200'
                  }`}>
                    {pkg.delivery_type || 'Drop Off'}
                  </span>
                  {pkg.total_fob != null && (
                    <span className="font-mono font-bold text-green-700 shrink-0">
                      ${parseFloat(pkg.total_fob).toFixed(2)} FOB
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <EncomiendaReceiptCell
                      pkg={pkg}
                      encPublicId={enc.unique_encomienda_id}
                      manifestId={manifestId}
                      onUploaded={onReceiptUploaded}
                    />
                    <EncomiendaPhotoCell
                      pkg={pkg}
                      encPublicId={enc.unique_encomienda_id}
                    />
                  </div>
                </div>
                {(pkg.staff_comments || '').trim() && (
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    marginTop: '-4px',
                    fontStyle: 'italic',
                  }}>
                    💬 {pkg.staff_comments}
                  </div>
                )}
              </React.Fragment>
            ))
          )}
          {/* Encomienda boxes — static inline display */}
          {encBoxes && encBoxes.length > 0 && (
            <div className="rounded-lg p-3 my-1" style={{ background: '#f8fafc' }}>
              <p style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: 6 }}>
                Cajas de Encomienda
              </p>
              {encBoxes.map(b => (
                <p key={b.box_number} style={{ fontSize: 12, color: '#6b7280', lineHeight: '1.6' }}>
                  Caja {b.box_number} · {b.weight_lb ? `${b.weight_lb} lbs` : '—'} · {b.dimensions || '—'}
                </p>
              ))}
            </div>
          )}

          <div className="pt-2 border-t border-orange-100">
            <button
              onClick={() => onEdit(enc.id)}
              className="text-xs font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1"
            >
              <Edit2 size={11} /> Editar Encomienda
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Live Manifest Body (reusable — used by open manifest and session view) ───
const LiveManifestBody = ({
  shipments, boxes, encomiendas,
  currentManifest,
  onUpdate, onDeleteShipment, onAddBox, onUpdateBox, onDeleteBox,
  onEncomiendaDeleted,
  onRefreshEncomiendas,
  user, userPermissions, t,
  isSessionView,
}) => {
  const [creatingBoxFor, setCreatingBoxFor] = useState(null);
  const [boxForm, setBoxForm] = useState({ weight: '', dims: '', comments: '', municipio: '', departamento: '', reference_point: '', boxful_state_id: '', boxful_city_id: '' });
  const [boxRecipientForm, setBoxRecipientForm] = useState({ name: '', phone: '', address: '', deliveryType: 'Drop Off', _customerId: '' });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [existingRecipients, setExistingRecipients] = useState([]);
  const [boxSaving, setBoxSaving] = useState(false);
  const [editingBoxId, setEditingBoxId] = useState(null);
  const [editingBoxForm, setEditingBoxForm] = useState({});

  useEffect(() => {
    if (!editingBoxId) return;
    // Only pre-fill if destinatario is empty — never overwrite existing values
    if (editingBoxForm?.recipient_name) return;

    let cancelled = false;
    (async () => {
      const currentTenantId = tenant?.id;
      if (!currentTenantId) return;

      const { data: bpRows } = await supabase
        .from('box_packages')
        .select('package_ref_id, package_type')
        .eq('tenant_id', currentTenantId)
        .eq('box_id', editingBoxId)
        .eq('package_type', 'encomienda')
        .limit(1);

      const encPkgId = bpRows?.[0]?.package_ref_id;
      if (!encPkgId || cancelled) return;

      const { data: encPkg } = await supabase
        .from('encomienda_packages')
        .select('recipient_name, recipient_phone, recipient_address, recipient_departamento, recipient_municipio, delivery_type')
        .eq('id', encPkgId)
        .maybeSingle();

      if (!encPkg || cancelled) return;

      // Fuzzy-fallback for legacy records where dept/mun columns are null
      let dept = encPkg.recipient_departamento || '';
      let mun = encPkg.recipient_municipio || '';
      if (!dept && encPkg.recipient_address) {
        const addr = encPkg.recipient_address.toLowerCase();
        const match = SV_DEPARTAMENTOS.find(d => addr.includes(d.name.toLowerCase()));
        if (match) {
          dept = match.name;
          if (!mun) {
            const munMatch = match.municipios.find(m => addr.includes(m.name.toLowerCase()));
            if (munMatch) mun = munMatch.name;
          }
        }
      }

      // Merge only into fields that are still blank; do not stomp anything the user just typed
      setEditingBoxForm(prev => ({
        ...prev,
        recipient_name:    prev.recipient_name    || encPkg.recipient_name    || '',
        recipient_phone:   prev.recipient_phone   || encPkg.recipient_phone   || '',
        recipient_address: prev.recipient_address || encPkg.recipient_address || '',
        departamento:      prev.departamento      || dept,
        municipio:         prev.municipio         || mun,
        delivery_type:     prev.delivery_type     || encPkg.delivery_type     || 'Drop Off',
      }));

      console.log('[LiveManifest] caja destinatario prefilled from encomienda pkg:', { editingBoxId, encPkgId, dept, mun });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBoxId]);

  const [editingEncBox, setEditingEncBox] = useState({});
  const [editingPkgsBoxId, setEditingPkgsBoxId] = useState(null);
  const [editPkgsLoading, setEditPkgsLoading] = useState(false);
  const [editPkgsData, setEditPkgsData] = useState({
    primaryClientId: null,
    packageType: 'casillero',
    allGroups: [],
    boxEncomiendaId: null,
    saveError: null,
    saving: false,
  });
  const [deleteSuccessMsg, setDeleteSuccessMsg] = useState('');
  const [analyzingShipmentId, setAnalyzingShipmentId] = useState(null);
  const [editingEncomiendaId, setEditingEncomiendaId] = useState(null);
  const [courierModal, setCourierModal] = useState(null); // { boxId, couriers, dispatching, error, success }

  const { tenant } = useTenant();
  const [sharedClientsOpen, setSharedClientsOpen] = useState(false);
  const [sharedClientChips, setSharedClientChips] = useState([]);
  const [availableSharedClients, setAvailableSharedClients] = useState({ sameType: [], otherType: [] });
  const [loadingSharedClients, setLoadingSharedClients] = useState(false);
  const [sharedClientsInsertError, setSharedClientsInsertError] = useState('');
  const [guiaWarnMsg, setGuiaWarnMsg] = useState('');
  const [primaryPackages, setPrimaryPackages] = useState([]);
  const [loadingPrimaryPackages, setLoadingPrimaryPackages] = useState(false);
  const [selectedPrimaryPackageIds, setSelectedPrimaryPackageIds] = useState([]);
  const [primaryPackagesFetched, setPrimaryPackagesFetched] = useState(false);
  const [primaryPackagesError, setPrimaryPackagesError] = useState(null);
  // The box the last save created, as the database numbered it.
  const [savedBox, setSavedBox] = useState(null);
  const [boxType, setBoxType] = useState('casillero');
  const [primaryGroupWeight, setPrimaryGroupWeight] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [encomiendaPrincipalClients, setEncomiendaPrincipalClients] = useState([]);

  useEffect(() => {
    if (boxType !== 'encomienda' || !creatingBoxFor || !currentManifest?.id || !tenant?.id) {
      setEncomiendaPrincipalClients([]);
      return;
    }
    const currentTenantId = tenant.id;
    supabase
      .from('encomienda_packages')
      .select('encomiendas!inner(customer_id)')
      .eq('tenant_id', currentTenantId)
      .eq('entry_manifest_id', currentManifest.id)
      .then(({ data }) => {
        const ids = [...new Set(
          (data || [])
            .map(row => row.encomiendas?.customer_id)
            .filter(Boolean)
        )];
        setEncomiendaPrincipalClients(ids);
      });
  }, [boxType, creatingBoxFor, currentManifest?.id, tenant?.id]);

  // Fire the packages fetch whenever the panel is open and we have a client
  // to query. The <select> onChange only fires on user change — this handles
  // initial open, tab-switch, and reactive re-fetches without duplicating logic.
  useEffect(() => {
    if (!creatingBoxFor || !currentManifest?.id || !tenant?.id) return;
    const clientId = boxRecipientForm._customerId || creatingBoxFor;
    if (!clientId) return;
    // For encomienda tab, wait until the principal-clients list has loaded so we
    // don't fire on a client the encomienda side can't resolve.
    if (boxType === 'encomienda' && encomiendaPrincipalClients.length === 0) return;
    fetchPrimaryPackages(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingBoxFor, boxType, boxRecipientForm._customerId, encomiendaPrincipalClients.length]);

  const handleDispatchBox = async (box) => {
    const dept = SV_DEPARTAMENTOS.find(d => d.name === box.departamento);
    const muni = dept?.municipios?.find(m => m.name === box.municipio);
    const toCityId = muni?.boxful_city_id;
    if (!toCityId) {
      alert('Este paquete no tiene departamento/municipio válido para despacho Boxful.');
      return;
    }
    setCourierModal({ boxId: box.box_id, couriers: null, dispatching: false, error: null, success: null });
    const { data, error } = await supabase.functions.invoke('boxful-quoter', { body: { toCityId } });
    if (error || !data?.success) {
      setCourierModal(prev => ({ ...prev, couriers: [], error: error?.message || data?.error || 'Error al obtener cotizaciones.' }));
      return;
    }
    setCourierModal(prev => ({ ...prev, couriers: data.couriers }));
  };

  const handleSelectCourier = async (courierId) => {
    const boxId = courierModal?.boxId;
    if (!boxId || !courierId) return;
    setCourierModal(prev => ({ ...prev, dispatching: true, error: null }));
    const { data, error } = await supabase.functions.invoke('boxful-dispatch', { body: { box_id: boxId, courierId } });
    if (error || !data?.success) {
      setCourierModal(prev => ({ ...prev, dispatching: false, error: error?.message || data?.error || 'Error al despachar.' }));
      return;
    }
    // After successful dispatch, mark boxful_release_state = 'dispatched'
    try {
      await supabase.from('boxes').update({ boxful_release_state: 'dispatched' }).eq('box_id', boxId);
      onUpdateBox(boxId, { boxful_release_state: 'dispatched' });
      console.log('[Boxful Dispatch] box=' + boxId + ' shipment=' + data.boxful_shipment_id);
    } catch (releaseErr) {
      console.error('[Boxful Dispatch] release state update error:', releaseErr);
    }
    setCourierModal(prev => ({
      ...prev,
      dispatching: false,
      success: `Despachado ✓  ID: ${data.boxful_shipment_id} · Mensajero: ${data.courier}`,
    }));
  };

  const handleMerge = async (masterUniquePackageId, duplicateId) => {
    if (window.confirm(`Are you sure you want to merge package ${duplicateId} into ${masterUniquePackageId}? This action cannot be undone.`)) {
      try {
        const duplicateItem = shipments.find(s => s.id === duplicateId);
        if (!duplicateItem) {
          throw new Error("Duplicate package not found for merge.");
        }
        const { error } = await supabase.rpc('merge_package_duplicate', {
          master_unique_package_id: masterUniquePackageId,
          duplicate_package_id: duplicateItem.unique_package_id
        });
        if (error) throw error;
        alert('Packages merged successfully!');
      } catch (error) {
        console.error('Error merging packages:', error);
        alert('Failed to merge packages: ' + error.message);
      }
    }
  };

  const fetchExistingRecipients = async (customerId) => {
    const { data } = await supabase
      .from('recipients')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });
    setExistingRecipients(data || []);
  };

  const applyAddress = (addr) => {
    setBoxRecipientForm(prev => ({
      ...prev,
      name:    addr.recipient_name  ?? prev.name,
      phone:   addr.recipient_phone ?? prev.phone,
      address: addr.direccion       ?? prev.address,
    }));
    setBoxForm(prev => ({
      ...prev,
      municipio:       addr.municipio         ?? prev.municipio,
      departamento:    addr.departamento      ?? prev.departamento,
      reference_point: addr.reference_point   ?? prev.reference_point,
      boxful_state_id: addr.boxful_state_id   ?? prev.boxful_state_id,
      boxful_city_id:  addr.boxful_city_id    ?? prev.boxful_city_id,
    }));
  };

  const fetchAvailableSharedClients = async () => {
    setLoadingSharedClients(true);
    try {
      const currentTenantId = tenant?.id;
      const principalId = boxRecipientForm._customerId || creatingBoxFor || null;

      const { data: casShipments } = await supabase
        .from('shipments')
        .select('customer_id')
        .eq('tenant_id', currentTenantId)
        .eq('manifest_id', currentManifest?.id);

      const { data: encEncomiendas } = await supabase
        .from('encomiendas')
        .select('customer_id')
        .eq('tenant_id', currentTenantId)
        .eq('manifest_id', currentManifest?.id);

      const casClientIds = [...new Set((casShipments || []).map(s => s.customer_id).filter(Boolean))];
      const encClientIds = [...new Set((encEncomiendas || []).map(e => e.customer_id).filter(Boolean))];

      const filteredCas = principalId ? casClientIds.filter(id => id !== principalId) : casClientIds;
      const filteredEnc = principalId ? encClientIds.filter(id => id !== principalId) : encClientIds;

      const allIds = [...new Set([...filteredCas, ...filteredEnc])];
      if (allIds.length === 0) {
        setAvailableSharedClients({ sameType: [], otherType: [] });
        return;
      }

      const { data: customers } = await supabase
        .from('customers')
        .select('id, full_name')
        .eq('tenant_id', tenant?.id)
        .filter('id', 'in', `(${allIds.join(',')})`);
      const customerMap = Object.fromEntries((customers || []).map(c => [c.id, c]));

      const sameIds   = boxType === 'casillero' ? filteredCas : filteredEnc;
      const otherIds  = boxType === 'casillero' ? filteredEnc : filteredCas;
      const samePT    = boxType;
      const otherPT   = boxType === 'casillero' ? 'encomienda' : 'casillero';

      setAvailableSharedClients({
        sameType:  sameIds.filter(id => customerMap[id]).map(id => ({ ...customerMap[id], package_type: samePT })),
        otherType: otherIds.filter(id => customerMap[id]).map(id => ({ ...customerMap[id], package_type: otherPT })),
      });
    } finally {
      setLoadingSharedClients(false);
    }
  };

  const fetchAvailablePackagesForClient = async (clientId, packageType) => {
    const currentTenantId = tenant?.id;
    if (!clientId || !currentManifest?.id || !currentTenantId) return [];

    // Load the client's own packages first, then look up box links only for
    // those ids. Never send the tenant-wide box_packages list in a URL — at
    // ~600+ links the not.in(...) GET exceeded the gateway limit and 400'd.
    const fail = (error) => {
      console.error('[BoxBuilder] candidate load failed', error);
      throw error;
    };

    let candidates;
    if (packageType === 'casillero') {
      const { data, error } = await supabase
        .from('shipments')
        .select('id, unique_package_id, weight_lb')
        .eq('tenant_id', currentTenantId)
        .eq('manifest_id', currentManifest.id)
        .eq('customer_id', clientId);
      if (error) fail(error);
      candidates = data || [];
    } else {
      // Query encomienda_packages directly, filter by entry_manifest_id (per-package),
      // and inner-join encomiendas to resolve client_id.
      const { data, error } = await supabase
        .from('encomienda_packages')
        .select('id, unique_package_id, package_number, encomienda_id, encomiendas!inner(customer_id)')
        .eq('tenant_id', currentTenantId)
        .eq('entry_manifest_id', currentManifest.id)
        .eq('encomiendas.customer_id', clientId);
      if (error) fail(error);
      candidates = (data || []).map(row => ({
        id: row.id,
        unique_package_id: row.unique_package_id,
        package_number: row.package_number,
        encomienda_id: row.encomienda_id,
      }));
    }

    let boxedIds = new Set();
    if (candidates.length > 0) {
      const { data: allocated, error } = await supabase
        .from('box_packages')
        .select('package_ref_id')
        .eq('tenant_id', currentTenantId)
        .eq('package_type', packageType === 'casillero' ? 'casillero' : 'encomienda')
        .in('package_ref_id', candidates.map(c => c.id));
      if (error) fail(error);
      boxedIds = new Set((allocated || []).map(r => String(r.package_ref_id)));
    }

    const available = candidates.filter(c => !boxedIds.has(String(c.id)));
    console.log(`[BoxBuilder] client=${clientId} manifest=${currentManifest.id} shipments=${candidates.length} boxed=${candidates.length - available.length} available=${available.length}`);
    return available;
  };

  const fetchPrimaryPackages = async (clientId) => {
    setLoadingPrimaryPackages(true);
    setPrimaryPackagesFetched(false);
    setSelectedPrimaryPackageIds([]);
    setPrimaryPackagesError(null);
    try {
      const pkgs = await fetchAvailablePackagesForClient(clientId, boxType);
      setPrimaryPackages(pkgs);
    } catch (error) {
      setPrimaryPackages([]);
      setPrimaryPackagesError(error);
    } finally {
      setLoadingPrimaryPackages(false);
      setPrimaryPackagesFetched(true);
    }
  };

  const fetchSavedAddresses = async (customerId) => {
    if (!customerId) { setSavedAddresses([]); setSelectedAddressId(''); return; }
    const { data, error } = await supabase
      .from('customer_addresses')
      .select('*')
      .eq('customer_id', customerId)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) {
      console.error('[CajaForm] fetchSavedAddresses error:', error);
      return;
    }
    console.log('[CajaForm] savedAddresses for', customerId, data);
    setSavedAddresses(data ?? []);
    const defaultAddr = data?.find(a => a.is_default) ?? data?.[0] ?? null;
    if (defaultAddr) {
      setSelectedAddressId(defaultAddr.id);
      applyAddress(defaultAddr);
    } else {
      setSelectedAddressId('');
    }
  };

  const saveEncBoxField = async (boxId, fields) => {
    if (!fields) return;
    const updates = {};
    if (fields.weight_lb !== undefined && fields.weight_lb !== '') {
      updates.weight_lb = parseFloat(fields.weight_lb) || null;
    }
    if (fields.dimensions !== undefined) {
      updates.dimensions = (fields.dimensions || '').trim() || null;
    }
    if (Object.keys(updates).length === 0) return;
    const { error } = await supabase.from('encomienda_boxes').update(updates).eq('id', boxId);
    if (error) {
      console.error('[saveEncBoxField] Error:', error);
    } else {
      setEditingEncBox(prev => { const n = { ...prev }; delete n[boxId]; return n; });
      await onRefreshEncomiendas();
    }
  };

  const handleDeleteEncBox = async (boxId) => {
    if (!window.confirm('¿Eliminar esta caja de encomienda? Esta acción no se puede deshacer.')) return;
    const currentTenantId = tenant?.id;
    const { error } = await supabase
      .from('encomienda_boxes')
      .delete()
      .eq('id', boxId)
      .eq('tenant_id', currentTenantId);
    if (error) {
      alert('Error eliminando caja: ' + error.message);
      return;
    }
    await onRefreshEncomiendas();
  };

  const canEditDelete = ['admin', 'manager', 'bodega'].includes(user?.role);

  const loadEditPkgsData = async (boxId) => {
    const currentTenantId = tenant?.id;
    if (!currentTenantId) return;
    setEditPkgsLoading(true);

    const boxObj = boxes.find(b => b.box_id === boxId);
    const manifestId = boxObj?.manifest_id || currentManifest?.id || null;

    // 1. Existing box_packages for this box
    const { data: bpRows } = await supabase
      .from('box_packages')
      .select('id, client_id, package_ref_id, is_primary, package_type')
      .eq('tenant_id', currentTenantId)
      .eq('box_id', boxId);
    const bpData = bpRows || [];
    const packageType = bpData[0]?.package_type || 'casillero';
    const primaryClientId = bpData.find(r => r.is_primary)?.client_id || null;
    const clientIds = [...new Set(bpData.map(r => r.client_id).filter(Boolean))];

    // 2. Customer names
    let nameMap = {};
    if (clientIds.length > 0) {
      const { data: custData } = await supabase
        .from('customers')
        .select('id, full_name')
        .eq('tenant_id', currentTenantId)
        .in('id', clientIds);
      for (const c of (custData || [])) nameMap[c.id] = c.full_name;
    }

    // 3. box_client_groups weights
    const { data: bcgData } = await supabase
      .from('box_client_groups')
      .select('client_id, weight_lb')
      .eq('tenant_id', currentTenantId)
      .eq('box_id', boxId);
    const weightMap = {};
    for (const r of (bcgData || [])) weightMap[r.client_id] = r.weight_lb;

    // 4. Packages locked in other boxes (same package_type) — scoped to the
    // client's own package ids so it never pulls the whole tenant's links.
    const fetchOtherBoxAllocated = async (pkgIds) => {
      if (pkgIds.length === 0) return new Set();
      const { data, error } = await supabase
        .from('box_packages')
        .select('package_ref_id, box_id')
        .eq('tenant_id', currentTenantId)
        .eq('package_type', packageType)
        .in('package_ref_id', pkgIds);
      if (error) console.error('[BoxBuilder] edit-flow box link load failed', error);
      return new Set(
        (data || [])
          .filter(r => r.box_id !== boxId)
          .map(r => String(r.package_ref_id))
      );
    };

    // 5. Per-client package lists
    const allGroups = await Promise.all(clientIds.map(async (clientId) => {
      const clientBpIds = new Set(
        bpData.filter(r => r.client_id === clientId).map(r => r.package_ref_id)
      );
      const isPrimary = bpData.some(r => r.client_id === clientId && r.is_primary);
      let packages = [];

      if (packageType === 'casillero') {
        const { data: shipData } = await supabase
          .from('shipments')
          .select('id, unique_package_id')
          .eq('tenant_id', currentTenantId)
          .eq('manifest_id', manifestId)
          .eq('customer_id', clientId);
        const otherBoxAllocated = await fetchOtherBoxAllocated((shipData || []).map(s => s.id));
        packages = (shipData || []).map(s => ({
          id: s.id,
          label: s.unique_package_id ?? s.id,
          checked: clientBpIds.has(s.id),
          lockedInOtherBox: otherBoxAllocated.has(String(s.id)) && !clientBpIds.has(s.id),
        }));
      } else {
        const { data: encData } = await supabase
          .from('encomiendas')
          .select('id, encomienda_packages(id, unique_package_id, package_number)')
          .eq('tenant_id', currentTenantId)
          .eq('manifest_id', manifestId)
          .eq('customer_id', clientId);
        const encPkgs = (encData || []).flatMap(enc => enc.encomienda_packages || []);
        const otherBoxAllocated = await fetchOtherBoxAllocated(encPkgs.map(p => p.id));
        packages = encPkgs.map(p => ({
          id: p.id,
          label: p.unique_package_id ?? (p.package_number != null ? `Paquete ${p.package_number}` : p.id),
          checked: clientBpIds.has(p.id),
          lockedInOtherBox: otherBoxAllocated.has(String(p.id)) && !clientBpIds.has(p.id),
        }));
      }

      return { client_id: clientId, full_name: nameMap[clientId] ?? clientId, is_primary: isPrimary, weight_lb: weightMap[clientId] ?? 0, packages };
    }));

    setEditPkgsData({ primaryClientId, packageType, allGroups, saveError: null, saving: false });
    setEditPkgsLoading(false);
  };

  const handleSaveEditPkgs = async () => {
    const currentTenantId = tenant?.id;
    if (!editingPkgsBoxId || !currentTenantId) return;
    setEditPkgsData(prev => ({ ...prev, saving: true, saveError: null }));
    try {
      // 1. Delete all existing box_packages for this box
      const { error: delErr } = await supabase
        .from('box_packages')
        .delete()
        .eq('tenant_id', currentTenantId)
        .eq('box_id', editingPkgsBoxId);
      if (delErr) throw delErr;

      // 2. Re-insert checked packages
      const { packageType, allGroups, primaryClientId } = editPkgsData;
      const inserts = allGroups.flatMap(group =>
        group.packages
          .filter(p => p.checked)
          .map(p => ({
            tenant_id: currentTenantId,
            box_id: editingPkgsBoxId,
            client_id: group.client_id,
            package_type: packageType,
            package_ref_id: p.id,
            is_primary: group.client_id === primaryClientId,
          }))
      );
      if (inserts.length > 0) {
        const { error: insErr } = await supabase.from('box_packages').insert(inserts);
        if (insErr) throw insErr;
      }

      // 3. Upsert box_client_groups weights
      for (const group of allGroups) {
        const { error: bcgErr } = await supabase
          .from('box_client_groups')
          .upsert(
            { tenant_id: currentTenantId, box_id: editingPkgsBoxId, client_id: group.client_id, weight_lb: parseFloat(group.weight_lb) || 0 },
            { onConflict: 'box_id,client_id' }
          );
        if (bcgErr) throw bcgErr;
      }

      // 4. Update boxes.weight_lb = sum of all group weights
      const totalWeight = allGroups.reduce((sum, g) => sum + (parseFloat(g.weight_lb) || 0), 0);
      const { error: boxErr } = await supabase
        .from('boxes')
        .update({ weight_lb: totalWeight })
        .eq('tenant_id', currentTenantId)
        .eq('box_id', editingPkgsBoxId);
      if (boxErr) throw boxErr;

      onUpdateBox(editingPkgsBoxId, { weight_lb: totalWeight });
      setEditingPkgsBoxId(null);
      setEditPkgsData({ primaryClientId: null, packageType: 'casillero', allGroups: [], saveError: null, saving: false });
    } catch (err) {
      console.error('[SaveEditPkgs]', err);
      setEditPkgsData(prev => ({ ...prev, saving: false, saveError: err.message }));
    }
  };

  const handleSaveBox = async (customerId) => {
    const totalWeight = primaryGroupWeight + sharedClientChips.reduce((sum, chip) => sum + (parseFloat(chip.weight_lb) || 0), 0);
    if (!totalWeight && !boxForm.dims) {
      alert('Ingrese el peso o dimensiones de la caja.');
      return;
    }

    const totalSelectedPackages =
      (selectedPrimaryPackageIds?.length || 0) +
      sharedClientChips.reduce((sum, c) => sum + (c.selected_package_ids?.length || 0), 0);

    if (totalSelectedPackages === 0) {
      alert('Debes seleccionar al menos un paquete antes de guardar la caja.');
      return;
    }

    setBoxSaving(true);
    setSavedBox(null);
    const newBoxId = () => 'BOX-' + customerId + '-' + Date.now();
    let boxId = newBoxId();

    let recipientId = null;
    if (boxRecipientForm.name.trim()) {
      const { data: newRecipient, error: recipientError } = await supabase
        .from('recipients')
        .insert([{
          full_name: boxRecipientForm.name.trim(),
          phone_number: boxRecipientForm.phone.replace(/\D/g, '') || null,
          address: boxRecipientForm.address.trim() || null,
          delivery_type: boxRecipientForm.deliveryType,
          customer_id: customerId,
        }])
        .select()
        .single();
      if (recipientError) {
        console.error('[SaveBox] Recipient insert error:', recipientError);
        alert('Error guardando destinatario: ' + recipientError.message);
        setBoxSaving(false);
        return;
      }
      recipientId = newRecipient.id;
    }

    console.log('[SaveBox] Attempting save:', {
      boxId, customerId,
      boxWeight: boxForm.weight,
      boxDimensions: boxForm.dims,
      recipientId,
    });

    // display_sequence and global_package_id are assigned by the DB trigger
    // and read back from the inserted row — never computed here.
    let insertedBox;
    try {
      insertedBox = await insertBox(supabase, {
        box_id: boxId,
        customer_id: customerId,
        weight_lb: totalWeight || null,
        dimensions: boxForm.dims.trim() || null,
        internal_comments: boxForm.comments.trim() || null,
        manifest_id: currentManifest?.id || null,
        recipient_id: recipientId,
        recipient_name:    (boxRecipientForm.name    || '').trim() || null,
        recipient_phone:   (boxRecipientForm.phone   || '').replace(/\D/g, '') || null,
        recipient_address: (boxRecipientForm.address || '').trim() || null,
        delivery_type:     boxRecipientForm.deliveryType || 'Drop Off',
        assignment_status: 'assigned',
        entry_manifest_id: currentManifest?.id || null,
        municipio:         (boxForm.municipio    || '').trim() || null,
        departamento:      (boxForm.departamento || '').trim() || null,
        reference_point:   (boxForm.reference_point || '').trim() || null,
      }, { regenerateId: newBoxId });
    } catch (boxError) {
      setBoxSaving(false);
      console.error('[SaveBox] Box insert error:', boxError);
      alert(boxError.message === BOX_NUMBER_ERROR ? BOX_NUMBER_ERROR : 'Error guardando caja: ' + boxError.message);
      return;
    }

    setBoxSaving(false);
    boxId = insertedBox.box_id; // a retry may have used a fresh id
    onAddBox(insertedBox);
    setSavedBox(insertedBox);
    try {
      const currentTenantId = tenant?.id;
      const { data: stopCheck, error: stopErr } = await supabase
        .from('guia_stops')
        .select('id, status')
        .eq('box_id', boxId)
        .eq('tenant_id', currentTenantId)
        .maybeSingle();
      if (stopErr || !stopCheck) {
        console.log('[GuiaSync] stop not found for box', boxId);
        setGuiaWarnMsg('Guía no pudo sincronizarse — contacta al administrador.');
        setTimeout(() => setGuiaWarnMsg(''), 8000);
      }
    } catch (guiaErr) {
      console.log('[GuiaSync] stop not found for box', boxId);
      setGuiaWarnMsg('Guía no pudo sincronizarse — contacta al administrador.');
      setTimeout(() => setGuiaWarnMsg(''), 8000);
    }

    const currentTenantId = tenant?.id;
    const allInserts = [
      ...selectedPrimaryPackageIds.map(pkgId => ({
        tenant_id: currentTenantId,
        box_id: boxId,
        client_id: customerId,
        package_type: boxType,
        package_ref_id: pkgId,
        is_primary: true,
      })),
      ...sharedClientChips.flatMap(chip =>
        chip.selected_package_ids.map(pkgId => ({
          tenant_id: currentTenantId,
          box_id: boxId,
          client_id: chip.client_id,
          package_type: chip.package_type,
          package_ref_id: pkgId,
          is_primary: false,
        }))
      ),
    ];
    if (allInserts.length > 0) {
      const { error: bpInsertError } = await supabase.from('box_packages').insert(allInserts);
      if (bpInsertError) {
        console.error('[SaveBox] box_packages insert error:', bpInsertError);
        await supabase.from('boxes').delete().eq('box_id', boxId).eq('tenant_id', currentTenantId);
        onDeleteBox(boxId);
        alert(`Error guardando paquetes: ${bpInsertError.message}. La caja fue eliminada para evitar inconsistencias.`);
        return;
      }
    }

    const groupInserts = [
      { tenant_id: currentTenantId, box_id: boxId, client_id: customerId, weight_lb: primaryGroupWeight || 0 },
      ...sharedClientChips.map(chip => ({
        tenant_id: currentTenantId,
        box_id: boxId,
        client_id: chip.client_id,
        weight_lb: parseFloat(chip.weight_lb) || 0,
      })),
    ];
    const { error: groupError } = await supabase.from('box_client_groups').insert(groupInserts);
    if (groupError) {
      console.error('[SaveBox] box_client_groups insert error:', groupError);
      await supabase.from('box_packages').delete().eq('box_id', boxId).eq('tenant_id', currentTenantId);
      await supabase.from('boxes').delete().eq('box_id', boxId).eq('tenant_id', currentTenantId);
      onDeleteBox(boxId);
      alert(`Error guardando grupos: ${groupError.message}. La caja fue eliminada para evitar inconsistencias.`);
      return;
    }

    setBoxForm({ weight: '', dims: '', comments: '', municipio: '', departamento: '', reference_point: '', boxful_state_id: '', boxful_city_id: '' });
    setBoxRecipientForm({ name: '', phone: '', address: '', deliveryType: 'Drop Off', _customerId: '' });
    setExistingRecipients([]);
    setSavedAddresses([]);
    setSelectedAddressId('');
    setSharedClientChips([]);
    setSharedClientsOpen(false);
    setAvailableSharedClients({ sameType: [], otherType: [] });
    setSharedClientsInsertError('');
    setPrimaryPackages([]);
    setSelectedPrimaryPackageIds([]);
    setPrimaryPackagesFetched(false);
    setPrimaryGroupWeight(0);
    setCreatingBoxFor(null);
  };

  // ── Billing guard before deleting a master box ─────────────────────────────
  // invoices_box_id_fkey is FOREIGN KEY (box_id) REFERENCES boxes(box_id) with
  // NO ACTION on delete — every other FK pointing at boxes cascades, so invoices
  // is the only blocker. Check billing state first; only detach invoices that
  // are genuinely inactive (void/draft with no payments recorded).
  const assertBoxDeletable = async (boxId) => {
    const result = { ok: false, reason: undefined, invoices: [], payments: [] };
    const numberOf = (inv) => inv.invoice_number ?? inv.numero_factura ?? inv.id;
    const statusOf = (inv) => inv.status ?? inv.estado ?? 'desconocido';

    const { data: invoiceRows, error: invError } = await supabase
      .from('invoices')
      .select('id, invoice_number, status, total, balance_due')
      .eq('box_id', boxId);

    if (invError) {
      console.log('[deleteBox] invoice check failed: ' + invError.message);
      result.reason = 'No se pudo verificar el estado de facturación — intentá de nuevo.';
      return result;
    }

    const invoices = invoiceRows || [];
    result.invoices = invoices;
    console.log(`[deleteBox] invoices found: ${invoices.length} status=[${invoices.map(statusOf).join(', ')}]`);

    if (invoices.length === 0) {
      result.ok = true;
      return result;
    }

    const blocking = invoices.filter(
      inv => !['void', 'draft'].includes(String(statusOf(inv)).toLowerCase())
    );
    if (blocking.length > 0) {
      blocking.forEach(inv => console.log('[deleteBox] blocked by invoice ' + numberOf(inv)));
      const numeros  = blocking.map(numberOf).join(', ');
      const estados  = [...new Set(blocking.map(statusOf))].join(', ');
      result.reason = `No se puede eliminar: esta encomienda tiene la factura ${numeros} (${estados}). Anulá la factura primero desde Facturación.`;
      return result;
    }

    // Every invoice is void or draft — a voided invoice with a recorded payment
    // is still not safe to detach.
    const invoiceIds = invoices.map(inv => inv.id);
    const { data: paymentRows, error: payError } = await supabase
      .from('invoice_payments')
      .select('id, amount, invoice_id')
      .in('invoice_id', invoiceIds);

    if (payError) {
      console.log('[deleteBox] invoice check failed: ' + payError.message);
      result.reason = 'No se pudo verificar el estado de facturación — intentá de nuevo.';
      return result;
    }

    const payments = paymentRows || [];
    result.payments = payments;
    if (payments.length > 0) {
      const pay = payments[0];
      const inv = invoices.find(i => i.id === (pay.invoice_id ?? pay.factura_id)) || invoices[0];
      console.log('[deleteBox] blocked by payment on voided invoice ' + numberOf(inv));
      result.reason = `No se puede eliminar: la factura anulada ${numberOf(inv)} todavía tiene un pago registrado de $${pay.amount ?? pay.monto ?? 0}. Revisá el pago antes de borrar.`;
      return result;
    }

    result.ok = true;
    return result;
  };

  // Single entry point for deleting a master box: confirm → billing guard →
  // detach inactive invoices → DELETE. Parent onDeleteBox only syncs UI state.
  const handleDeleteBoxGuarded = async (boxId) => {
    if (!window.confirm('Are you sure you want to delete this master box?')) return;

    let check;
    try {
      check = await assertBoxDeletable(boxId);
    } catch (err) {
      console.log('[deleteBox] invoice check failed: ' + err.message);
      alert('No se pudo verificar el estado de facturación — intentá de nuevo.');
      return;
    }
    if (!check.ok) {
      alert(check.reason);
      return;
    }

    if (check.invoices.length > 0) {
      const { data: detached, error: detachError } = await supabase
        .from('invoices')
        .update({ box_id: null })
        .eq('box_id', boxId)
        .select();
      if (detachError) {
        console.log('[deleteBox] invoice check failed: ' + detachError.message);
        alert('No se pudo verificar el estado de facturación — intentá de nuevo.');
        return;
      }
      // RLS returns zero rows silently when it blocks — never fall through.
      if (!detached || detached.length < 1) {
        alert('No se pudo completar la operación — verificá permisos.');
        return;
      }
      console.log(`[deleteBox] detached ${detached.length} inactive invoices`);
    }

    const { data: deleted, error: deleteError } = await supabase
      .from('boxes')
      .delete()
      .eq('box_id', boxId)
      .select();
    if (deleteError) {
      alert('Error al eliminar: ' + deleteError.message);
      return;
    }
    if (!deleted || deleted.length < 1) {
      alert('No se pudo completar la operación — verificá permisos.');
      return;
    }
    console.log('[deleteBox] deleted ' + boxId);
    onDeleteBox(boxId);
  };

  // ── Billing guard before deleting an encomienda ────────────────────────────
  // El DELETE corre contra `encomiendas` y Postgres cascadea a `boxes`
  // (boxes.encomienda_id → encomiendas.id ON DELETE CASCADE). Ese borrado
  // cascadeado del bulto es el que viola invoices_box_id_fkey — FOREIGN KEY
  // (box_id) REFERENCES boxes(box_id) con NO ACTION —, así que la guardia tiene
  // que correr sobre TODOS los bultos de la encomienda, no sobre uno solo.
  // Sin bultos no hay nada que bloquee el cascade.
  const assertEncomiendaDeletable = async (encomiendaId) => {
    const result = { ok: false, reason: undefined, boxes: [], invoices: [], payments: [] };
    const numberOf = (inv) => inv.invoice_number ?? inv.numero_factura ?? inv.id;
    const statusOf = (inv) => inv.status ?? inv.estado ?? 'desconocido';

    const { data: boxRows, error: boxError } = await supabase
      .from('boxes')
      .select('box_id')
      .eq('encomienda_id', encomiendaId);

    if (boxError) {
      console.log('[deleteEncomienda] check failed: ' + boxError.message);
      result.reason = 'No se pudo verificar el estado de facturación — intentá de nuevo.';
      return result;
    }

    const boxes = boxRows || [];
    result.boxes = boxes;
    console.log(`[deleteEncomienda] boxes resolved: ${boxes.length}`);

    if (boxes.length === 0) {
      result.ok = true;
      return result;
    }

    const boxIds = boxes.map(box => box.box_id ?? box.id);

    const { data: invoiceRows, error: invError } = await supabase
      .from('invoices')
      .select('id, invoice_number, status, total, balance_due, box_id')
      .in('box_id', boxIds);

    if (invError) {
      console.log('[deleteEncomienda] check failed: ' + invError.message);
      result.reason = 'No se pudo verificar el estado de facturación — intentá de nuevo.';
      return result;
    }

    const invoices = invoiceRows || [];
    result.invoices = invoices;
    console.log(`[deleteEncomienda] invoices found: ${invoices.length} status=[${invoices.map(statusOf).join(', ')}]`);

    if (invoices.length === 0) {
      result.ok = true;
      return result;
    }

    const blocking = invoices.filter(
      inv => !['void', 'draft'].includes(String(statusOf(inv)).toLowerCase())
    );
    if (blocking.length > 0) {
      blocking.forEach(inv => console.log('[deleteEncomienda] blocked by invoice ' + numberOf(inv)));
      const numeros = blocking.map(numberOf).join(', ');
      const estados = [...new Set(blocking.map(statusOf))].join(', ');
      result.reason = `No se puede eliminar: esta encomienda tiene la factura ${numeros} (${estados}). Anulá la factura primero desde Facturación.`;
      return result;
    }

    // Todas void/draft — una factura anulada que todavía tiene un pago
    // registrado no es segura para cascadear.
    const invoiceIds = invoices.map(inv => inv.id);
    const { data: paymentRows, error: payError } = await supabase
      .from('invoice_payments')
      .select('id, amount, invoice_id')
      .in('invoice_id', invoiceIds);

    if (payError) {
      console.log('[deleteEncomienda] check failed: ' + payError.message);
      result.reason = 'No se pudo verificar el estado de facturación — intentá de nuevo.';
      return result;
    }

    const payments = paymentRows || [];
    result.payments = payments;
    if (payments.length > 0) {
      const pay = payments[0];
      const inv = invoices.find(i => i.id === (pay.invoice_id ?? pay.factura_id)) || invoices[0];
      console.log('[deleteEncomienda] blocked by payment on voided invoice ' + numberOf(inv));
      result.reason = `No se puede eliminar: la factura anulada ${numberOf(inv)} todavía tiene un pago registrado de $${pay.amount ?? pay.monto ?? 0}. Revisá el pago antes de borrar.`;
      return result;
    }

    result.ok = true;
    return result;
  };

  const handleDeleteEncomienda = async (encomiendaId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      alert('Sesión expirada. Recarga la página.');
      return;
    }

    if (user?.role !== 'admin') {
      const enteredPin = window.prompt(
        'Ingrese el PIN de cierre para confirmar eliminación:'
      );
      if (!enteredPin) return;
      const { data: staff } = await supabase
        .from('staff_members')
        .select('closing_pin')
        .eq('username', user.username)
        .maybeSingle();
      if (!staff || staff.closing_pin !== enteredPin) {
        alert('PIN incorrecto.');
        return;
      }
    }

    if (!window.confirm('¿Eliminar esta encomienda? Esta acción no se puede deshacer.')) return;

    let check;
    try {
      check = await assertEncomiendaDeletable(encomiendaId);
    } catch (err) {
      console.log('[deleteEncomienda] check failed: ' + err.message);
      alert('No se pudo verificar el estado de facturación — intentá de nuevo.');
      return;
    }
    if (!check.ok) {
      alert(check.reason);
      return;
    }

    // Solo se desprenden facturas inactivas (void/draft sin pagos): la guardia
    // ya abortó cualquier otro caso. balance_due es columna generada — jamás se
    // escribe; acá solo se pone box_id en null.
    if (check.invoices.length > 0) {
      const boxIds = check.boxes.map(box => box.box_id ?? box.id);
      const { data: detached, error: detachError } = await supabase
        .from('invoices')
        .update({ box_id: null })
        .in('box_id', boxIds)
        .select();
      if (detachError) {
        console.log('[deleteEncomienda] check failed: ' + detachError.message);
        alert('No se pudo verificar el estado de facturación — intentá de nuevo.');
        return;
      }
      // RLS devuelve cero filas en silencio cuando bloquea — nunca seguir de largo.
      if (!detached || detached.length < 1) {
        alert('No se pudo completar la operación — verificá permisos.');
        return;
      }
      console.log(`[deleteEncomienda] detached ${detached.length} inactive invoices`);
    }

    const { data: deleted, error } = await supabase
      .from('encomiendas')
      .delete()
      .eq('id', encomiendaId)
      .select();

    if (error) {
      alert('Error al eliminar: ' + error.message);
      return;
    }
    if (!deleted || deleted.length < 1) {
      alert('No se pudo completar la operación — verificá permisos.');
      return;
    }
    console.log('[deleteEncomienda] deleted ' + encomiendaId);

    onEncomiendaDeleted(encomiendaId);
  };

  // Filter boxes to this manifest (session view passes pre-fetched boxes, open view passes all)
  const manifestBoxes = currentManifest?.id
    ? boxes.filter(b => b.manifest_id === currentManifest.id)
    : boxes;

  const allCustomerIds = [...new Set([
    ...shipments.map(s => s.customer_id),
    ...manifestBoxes.map(b => b.customer_id),
    ...encomiendas.map(e => e.customer_id),
  ])];

  const groups = allCustomerIds.reduce((acc, id) => {
    if (!id) return acc;
    const custEncomiendas = encomiendas.filter(e => e.customer_id === id);
    acc[id] = {
      items: shipments.filter(s => s.customer_id === id),
      boxes: manifestBoxes.filter(b => b.customer_id === id),
      encomiendas: custEncomiendas,
      encBoxes: custEncomiendas.flatMap(enc =>
        (enc.encomienda_boxes || []).map(b => ({ ...b, encomiendaId: enc.unique_encomienda_id }))
      ),
    };
    return acc;
  }, {});

  // ── DIAGNOSTIC LOGS ────────────────────────────────────────────────────────
  console.log('[Groups] Shipment customer_ids:', shipments.map(s => s.customer_id));
  console.log('[Groups] Encomienda customer_ids:', encomiendas.map(e => e.customer_id));
  console.log('[Groups] All group keys:', Object.keys(groups));
  encomiendas.forEach(enc => {
    console.log(
      '[Groups] Enc', enc.unique_encomienda_id,
      'customer_id:', JSON.stringify(enc.customer_id),
      '| found in groups:', !!groups[enc.customer_id],
      '| group encomiendas count:', groups[enc.customer_id]?.encomiendas?.length ?? 'NO GROUP'
    );
  });
  // ── END DIAGNOSTIC LOGS ───────────────────────────────────────────────────

  return (
    <div>
      {savedBox && (
        <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 text-sm font-medium px-4 py-3 rounded-xl">
          <span>✓</span> CAJA #{savedBox.display_sequence} guardada · {savedBox.global_package_id}
          <button type="button" onClick={() => setSavedBox(null)} className="ml-auto text-green-700" aria-label="Cerrar">✕</button>
        </div>
      )}

      {deleteSuccessMsg && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 text-sm font-medium px-4 py-3 rounded-xl">
          <span>✓</span> {deleteSuccessMsg}
        </div>
      )}

      {guiaWarnMsg && (
        <div style={{
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#fefce8',
          border: '1px solid #fde047',
          color: '#854d0e',
          fontSize: '13px',
          fontWeight: '500',
          padding: '10px 14px',
          borderRadius: '12px',
        }}>
          <span>⚠️</span> {guiaWarnMsg}
        </div>
      )}

      {/* Empty state */}
      {Object.keys(groups).length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">📦</div>
          <p className="font-medium text-sm text-gray-500">
            {t?.emptyManifest || 'No packages in this manifest yet.'}
          </p>
        </div>
      ) : (
          <div className="space-y-8">
          {Object.entries(groups).map(([idPrefix, group]) => (
            <div key={idPrefix} className="tile" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Customer header */}
              <div className="bg-gray-50 p-4 border-b font-bold flex justify-between items-center text-blue-900" style={{ background: 'var(--bg)' }}>
                <span className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm" style={{ background: 'var(--blue)', color: '#fff' }}>{idPrefix}</span>
                <span className="text-xs bg-blue-100 px-3 py-1 rounded-full" style={{ background: 'var(--blue-soft)', color: 'var(--blue-text)' }}>
                  {group.items.length} Packages / {group.boxes.length + group.encBoxes.length} Boxes
                  {group.encomiendas.length > 0 && ` / ${group.encomiendas.length} Enc.`}
                </span>
              </div>

              {/* Two-column body */}
              <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-100" style={{ borderColor: 'var(--border)' }}>
                {/* Left column: packages + encomiendas */}
                <div className="md:w-3/5">
                  <div className="px-4 py-2 border-b border-gray-100" style={{ borderColor: 'var(--border)' }}>
                    <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)' }}>
                      Paquetes y Encomiendas
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100" style={{ borderColor: 'var(--border)' }}>
                    {group.items.map((item) => (
                      <ManifestRow
                        key={item.id}
                        item={item}
                        onUpdate={onUpdate}
                        onDeleteShipment={onDeleteShipment}
                        handleMerge={handleMerge}
                        user={user}
                        userPermissions={userPermissions}
                        onDeleteSuccess={msg => { setDeleteSuccessMsg(msg); setTimeout(() => setDeleteSuccessMsg(''), 6000); }}
                        t={t}
                        setAnalyzingShipmentId={setAnalyzingShipmentId}
                      />
                    ))}
                    {group.encomiendas.map(enc => (
                      <EncomiendaManifestRow
                        key={enc.id}
                        enc={enc}
                        manifestId={enc.manifest_id}
                        onEdit={(id) => setEditingEncomiendaId(id)}
                        onDelete={handleDeleteEncomienda}
                        onReceiptUploaded={onRefreshEncomiendas}
                      />
                    ))}
                    {group.items.length === 0 && group.encomiendas.length === 0 && (
                      <p className="empty-state p-4">Sin paquetes.</p>
                    )}
                  </div>
                </div>

                {/* Right column: master boxes */}
                <div className="md:w-2/5 bg-slate-50/60" style={{ background: 'var(--bg)' }}>
                  <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                    <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)' }}>
                      Master Boxes
                    </span>
                    {creatingBoxFor !== idPrefix && (
                      <button
                        onClick={() => {
                          setCreatingBoxFor(idPrefix);
                          fetchExistingRecipients(idPrefix);
                          fetchSavedAddresses(idPrefix);
                          setSharedClientChips([]);
                          setSharedClientsOpen(false);
                          setAvailableSharedClients({ sameType: [], otherType: [] });
                          setSharedClientsInsertError('');
                          setPrimaryPackages([]);
                          setSelectedPrimaryPackageIds([]);
                          setPrimaryPackagesFetched(false);
                          setPrimaryPackagesError(null);
                        }}
                        className="btn-ghost"
                        style={{ fontSize: '10px', color: 'var(--blue-text)', fontWeight: 'bold' }}
                      >
                        <PlusCircle size={11}/> Nueva Caja
                      </button>
                    )}
                  </div>

                  <div className="p-3 space-y-2">
                    {group.boxes.map(box => (
                      editingBoxId === box.box_id ? (
                        <div key={box.box_id} className="tile" style={{ padding: '12px' }}>
                          <h4 className="tile-title mb-2">Editar Caja Master</h4>

                          {/* Peso + Dimensiones */}
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input className="input" style={{ flex: 1 }} placeholder="Peso (lbs)" value={editingBoxForm.weight_lb ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, weight_lb: e.target.value})} />
                            <input className="input" style={{ flex: 2 }} placeholder="Dimensiones (ej: 12x8x10)" value={editingBoxForm.dimensions ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, dimensions: e.target.value})} />
                          </div>

                          {/* Internal Comments */}
                          <textarea className="input" style={{ width: '100%', height: '56px', marginBottom: '8px' }} placeholder="Internal Comments" value={editingBoxForm.internal_comments ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, internal_comments: e.target.value})} />

                          {/* Destinatario */}
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Destinatario</p>
                          <input className="input" style={{ width: '100%', marginBottom: '6px' }} placeholder="Nombre del destinatario" value={editingBoxForm.recipient_name ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, recipient_name: e.target.value})} />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                            <input className="input" placeholder="Teléfono" value={editingBoxForm.recipient_phone ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, recipient_phone: e.target.value})} />
                            <input className="input" placeholder="Dirección" value={editingBoxForm.recipient_address ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, recipient_address: e.target.value})} />
                          </div>

                          {/* Departamento + Municipio */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                            <select
                              className="input"
                              value={editingBoxForm.departamento ?? ''}
                              onChange={e => setEditingBoxForm({ ...editingBoxForm, departamento: e.target.value, municipio: '' })}
                            >
                              <option value="">-- Departamento --</option>
                              {SV_DEPARTAMENTOS.map(dep => (
                                <option key={dep.name} value={dep.name}>{dep.name}</option>
                              ))}
                            </select>
                            <select
                              className="input"
                              value={editingBoxForm.municipio ?? ''}
                              disabled={!editingBoxForm.departamento}
                              onChange={e => setEditingBoxForm({ ...editingBoxForm, municipio: e.target.value })}
                            >
                              <option value="">-- Municipio --</option>
                              {(SV_DEPARTAMENTOS.find(d => d.name === editingBoxForm.departamento)?.municipios || []).map(mun => (
                                <option key={mun.name} value={mun.name}>{mun.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Punto de Referencia */}
                          <input className="input" style={{ width: '100%', marginBottom: '6px' }} placeholder="Punto de referencia" value={editingBoxForm.reference_point ?? ''} onChange={e => setEditingBoxForm({...editingBoxForm, reference_point: e.target.value})} />

                          {/* Delivery Type */}
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                            <button type="button" onClick={() => setEditingBoxForm({...editingBoxForm, delivery_type: 'Pick Up'})} className={`btn ${editingBoxForm.delivery_type === 'Pick Up' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, justifyContent: 'center' }}>📦 Pick Up</button>
                            <button type="button" onClick={() => setEditingBoxForm({...editingBoxForm, delivery_type: 'Drop Off'})} className={`btn ${editingBoxForm.delivery_type === 'Drop Off' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, justifyContent: 'center', background: editingBoxForm.delivery_type === 'Drop Off' ? 'var(--green)' : '' }}>🚚 Drop Off</button>
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={async () => {
                              const currentTenantId = tenant?.id;
                              const updates = {
                                weight_lb:         parseFloat(editingBoxForm.weight_lb) || null,
                                dimensions:        (editingBoxForm.dimensions        ?? '').trim() || null,
                                internal_comments: (editingBoxForm.internal_comments ?? '').trim() || null,
                                recipient_name:    (editingBoxForm.recipient_name    ?? '').trim() || null,
                                recipient_phone:   (editingBoxForm.recipient_phone   ?? '').replace(/\D/g, '') || null,
                                recipient_address: (editingBoxForm.recipient_address ?? '').trim() || null,
                                delivery_type:     editingBoxForm.delivery_type || null,
                                departamento:      (editingBoxForm.departamento      ?? '').trim() || null,
                                municipio:         (editingBoxForm.municipio         ?? '').trim() || null,
                                reference_point:   (editingBoxForm.reference_point   ?? '').trim() || null,
                              };
                              await supabase.from('boxes').update(updates).eq('tenant_id', currentTenantId).eq('box_id', editingBoxId);
                              onUpdateBox(editingBoxId, updates);
                              setEditingBoxId(null);
                            }} className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>Guardar</button>
                            <button onClick={() => setEditingBoxId(null)} className="btn btn-ghost">Cancelar</button>
                          </div>
                          <div style={{ marginTop: '6px' }}>
                            <button
                              type="button"
                              onClick={() => { loadEditPkgsData(editingBoxId); setEditingPkgsBoxId(editingBoxId); }}
                              className="btn btn-ghost"
                              style={{ width: '100%', justifyContent: 'center', border: '1px solid var(--border)' }}
                            >
                              📦 Editar Paquetes asignados
                            </button>
                          </div>
                        </div>
                      ) : (
                        <BoxCard
                          key={box.box_id}
                          box={box}
                          user={user}
                          onEdit={() => { setEditingBoxId(box.box_id); setEditingBoxForm(box); }}
                          onDelete={() => handleDeleteBoxGuarded(box.box_id)}
                          onDispatch={() => handleDispatchBox(box)}
                          onUpdateBox={onUpdateBox}
                        />
                      )
                    ))}

                    {/* Encomienda boxes — editable */}
                    {group.encBoxes.map(box => (
                      <div key={box.id} className="row-item" style={{ background: 'var(--tile)', position: 'relative' }}>
                        <span className="badge badge-pending">ENC</span>
                        <span className="row-id" style={{ width: 'auto', minWidth: '60px' }}>Caja {box.box_number}</span>
                        <input
                          type="number"
                          className="input"
                          placeholder="lbs"
                          value={editingEncBox[box.id]?.weight_lb ?? (box.weight_lb ?? '')}
                          onChange={e => setEditingEncBox(prev => ({ ...prev, [box.id]: { ...prev[box.id], weight_lb: e.target.value } }))}
                          onBlur={() => saveEncBoxField(box.id, editingEncBox[box.id])}
                          style={{ width: 56, textAlign: 'right' }}
                        />
                        <span className="metric-sub" style={{ marginTop: 0 }}>lbs</span>
                        <input
                          type="text"
                          className="input"
                          placeholder="Dimensiones"
                          value={editingEncBox[box.id]?.dimensions ?? (box.dimensions ?? '')}
                          onChange={e => setEditingEncBox(prev => ({ ...prev, [box.id]: { ...prev[box.id], dimensions: e.target.value } }))}
                          onBlur={() => saveEncBoxField(box.id, editingEncBox[box.id])}
                          style={{ flex: 1 }}
                        />
                        {canEditDelete && (
                          <>
                            <button
                              type="button"
                              title="Guardar cambios"
                              onClick={() => saveEncBoxField(box.id, editingEncBox[box.id])}
                              style={{ color: '#93c5fd', padding: '2px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                              <Edit size={12} />
                            </button>
                            <button
                              type="button"
                              title="Eliminar caja"
                              onClick={() => handleDeleteEncBox(box.id)}
                              style={{ color: '#fca5a5', padding: '2px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}

                    {group.boxes.length === 0 && group.encBoxes.length === 0 && creatingBoxFor !== idPrefix && (
                      <p className="empty-state">Sin cajas.</p>
                    )}

                    {/* Summary bar — always visible */}
                    {(() => {
                      const totalBoxes = group.boxes.length + group.encBoxes.length;
                      const masterWeight = group.boxes.reduce((s, b) => s + (parseFloat(b.weight_lb) || 0), 0);
                      const encWeight = group.encBoxes.reduce((s, b) => s + (parseFloat(editingEncBox[b.id]?.weight_lb ?? b.weight_lb) || 0), 0);
                      const totalWeight = masterWeight + encWeight;
                      return (
                        <div className="metric-sub" style={{ paddingTop: '8px', borderTop: '1px solid var(--border)', marginTop: '8px' }}>
                          Total: {totalBoxes} caja{totalBoxes !== 1 ? 's' : ''} · {totalWeight.toFixed(1)} lbs
                        </div>
                      );
                    })()}

                    {creatingBoxFor === idPrefix && (
                      <div className="tile space-y-3" style={{ padding: '12px' }}>
                        <h4 className="tile-title">Nueva Caja Master</h4>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                          <button
                            type="button"
                            onClick={() => { setBoxType('casillero'); setBoxRecipientForm(prev => ({ ...prev, _customerId: '' })); setSelectedPrimaryPackageIds([]); setPrimaryPackages([]); setPrimaryPackagesFetched(false); setSharedClientChips([]); setPrimaryGroupWeight(0); }}
                            style={{ flex: 1, padding: '5px 0', fontSize: '11px', fontWeight: '600', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: boxType === 'casillero' ? 'var(--blue-soft)' : 'transparent', color: boxType === 'casillero' ? 'var(--blue-text)' : 'var(--text-secondary)' }}
                          >
                            📦 Casillero
                          </button>
                          <button
                            type="button"
                            onClick={() => { setBoxType('encomienda'); setBoxRecipientForm(prev => ({ ...prev, _customerId: '' })); setSelectedPrimaryPackageIds([]); setPrimaryPackages([]); setPrimaryPackagesFetched(false); setSharedClientChips([]); setPrimaryGroupWeight(0); }}
                            style={{ flex: 1, padding: '5px 0', fontSize: '11px', fontWeight: '600', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: boxType === 'encomienda' ? 'var(--blue-soft)' : 'transparent', color: boxType === 'encomienda' ? 'var(--blue-text)' : 'var(--text-secondary)' }}
                          >
                            🎁 Encomienda
                          </button>
                        </div>
                        <div style={{
                          background: 'var(--blue-soft)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '6px 10px',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: 'var(--blue-text)',
                          letterSpacing: '0.04em',
                          display: 'flex',
                          alignItems: 'center',
                          
                          gap: '8px',
                        }}>
                          NUEVA CAJA · el número se asigna al guardar
                          {sharedClientChips.length > 0 && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: '700',
                              padding: '2px 8px',
                              borderRadius: '99px',
                              background: '#00D4B8',
                              color: '#fff',
                              letterSpacing: '0',
                            }}>
                              📦 Caja Compartida
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="metric-label" style={{ fontSize: '10px' }}>Cliente Principal</p>
                          <select
                            className="input"
                            value={boxRecipientForm._customerId || idPrefix}
                            onChange={e => { setBoxRecipientForm({ ...boxRecipientForm, _customerId: e.target.value }); fetchSavedAddresses(e.target.value); fetchPrimaryPackages(e.target.value); }}
                          >
                            {(boxType === 'casillero'
                              ? [...new Set(shipments.map(s => s.customer_id).filter(Boolean))].sort()
                              : encomiendaPrincipalClients
                            ).map(cid => (
                              <option key={cid} value={cid}>{cid}</option>
                            ))}
                          </select>
                        </div>

                        {/* Primary packages picker */}
                        <div>
                          <p className="metric-label" style={{ fontSize: '10px', marginBottom: '4px' }}>Paquetes a incluir</p>
                          {loadingPrimaryPackages ? (
                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Cargando paquetes...</p>
                          ) : primaryPackagesError ? (
                            <p style={{ fontSize: '11px', color: 'var(--avianca-red)', fontStyle: 'italic' }}>
                              Error al cargar paquetes — intente de nuevo
                            </p>
                          ) : primaryPackagesFetched && primaryPackages.length === 0 ? (
                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              Este cliente ya tiene todos sus paquetes asignados a cajas
                            </p>
                          ) : primaryPackages.length > 0 ? (
                            <div style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                                <input
                                  type="checkbox"
                                  checked={primaryPackages.length > 0 && selectedPrimaryPackageIds.length === primaryPackages.length}
                                  onChange={e => setSelectedPrimaryPackageIds(e.target.checked ? primaryPackages.map(p => p.id) : [])}
                                />
                                Agregar todos ({primaryPackages.length})
                              </label>
                              {primaryPackages.map(pkg => (
                                <label key={pkg.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedPrimaryPackageIds.includes(pkg.id)}
                                    onChange={e => setSelectedPrimaryPackageIds(prev =>
                                      e.target.checked ? [...prev, pkg.id] : prev.filter(id => id !== pkg.id)
                                    )}
                                  />
                                  <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{pkg.unique_package_id ?? (pkg.package_number != null ? `Paquete ${pkg.package_number}` : pkg.id)}</span>
                                  {pkg.weight_lb != null && <span style={{ color: 'var(--text-secondary)' }}>{pkg.weight_lb} lb</span>}
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <p className="metric-label" style={{ fontSize: '10px', marginBottom: '4px' }}>Peso del grupo principal (lbs)</p>
                          <input
                            className="input"
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="0.0"
                            value={primaryGroupWeight === 0 ? '' : primaryGroupWeight}
                            onChange={e => setPrimaryGroupWeight(parseFloat(e.target.value) || 0)}
                            onBlur={e => { if (e.target.value === '') setPrimaryGroupWeight(0); }}
                          />
                        </div>
                        <div className="flex gap-2">
                          <div style={{ flex: 1 }}>
                            <p className="metric-label" style={{ fontSize: '10px', marginBottom: '4px' }}>Peso total (calculado)</p>
                            <input
                              className="input"
                              readOnly
                              style={{ background: 'var(--bg)', color: 'var(--text-secondary)', cursor: 'default' }}
                              value={(primaryGroupWeight + sharedClientChips.reduce((s, c) => s + (parseFloat(c.weight_lb) || 0), 0)).toFixed(1) + ' lbs'}
                            />
                          </div>
                          <input className="input" style={{ flex: 2 }} placeholder="Dimensiones (ej: 12x8x10)" value={boxForm.dims} onChange={e => setBoxForm({...boxForm, dims: e.target.value})} />
                        </div>
                        <textarea className="input" style={{ height: '60px' }} placeholder="Internal Comments" value={boxForm.comments} onChange={e => setBoxForm({...boxForm, comments: e.target.value})} />

                        {/* Shared clients collapsible */}
                        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const next = !sharedClientsOpen;
                              setSharedClientsOpen(next);
                              if (next && availableSharedClients.sameType.length === 0 && availableSharedClients.otherType.length === 0 && !loadingSharedClients) {
                                fetchAvailableSharedClients();
                              }
                            }}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '7px 10px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: 'var(--blue-text)',
                            }}
                          >
                            <span>+ Agregar cliente a caja compartida</span>
                            {sharedClientsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          </button>

                          {sharedClientsOpen && (
                            <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
                              {loadingSharedClients ? (
                                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Cargando clientes...</p>
                              ) : (
                                <>
                                  <select
                                    className="input"
                                    value=""
                                    onChange={e => {
                                      const rawValue = e.target.value;
                                      if (!rawValue) return;
                                      const colonIdx = rawValue.indexOf(':');
                                      const packageType = rawValue.substring(0, colonIdx);
                                      const cid = rawValue.substring(colonIdx + 1);
                                      const allClients = [...availableSharedClients.sameType, ...availableSharedClients.otherType];
                                      const customer = allClients.find(c => c.id === cid && c.package_type === packageType);
                                      if (!customer || sharedClientChips.some(ch => ch.client_id === cid && ch.package_type === packageType)) return;
                                      const label = `${cid} — ${customer.full_name ?? customer.id}`;
                                      setSharedClientChips(prev => [...prev, { client_id: cid, package_type: packageType, customerLabel: label, selected_package_ids: [], packages: [], loadingPackages: true, weight_lb: 0 }]);
                                      fetchAvailablePackagesForClient(cid, packageType).then(pkgs => {
                                        setSharedClientChips(prev => prev.map(ch => ch.client_id === cid && ch.package_type === packageType ? { ...ch, packages: pkgs, loadingPackages: false, loadError: null } : ch));
                                      }).catch(error => {
                                        setSharedClientChips(prev => prev.map(ch => ch.client_id === cid && ch.package_type === packageType ? { ...ch, packages: [], loadingPackages: false, loadError: error } : ch));
                                      });
                                    }}
                                  >
                                    <option value="">— Seleccionar cliente compartido —</option>
                                    {availableSharedClients.sameType.length > 0 && (
                                      <optgroup label={boxType === 'casillero' ? '📦 Casilleros' : '🎁 Encomiendas'}>
                                        {availableSharedClients.sameType
                                          .filter(c => !sharedClientChips.some(ch => ch.client_id === c.id && ch.package_type === c.package_type))
                                          .map(c => (
                                            <option key={`same-${c.id}`} value={`${c.package_type}:${c.id}`}>
                                              {c.id} — {c.full_name ?? ''}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}
                                    {availableSharedClients.otherType.length > 0 && (
                                      <optgroup label={boxType === 'casillero' ? '🎁 Encomiendas restantes' : '📦 Casilleros restantes'}>
                                        {availableSharedClients.otherType
                                          .filter(c => !sharedClientChips.some(ch => ch.client_id === c.id && ch.package_type === c.package_type))
                                          .map(c => (
                                            <option key={`other-${c.id}`} value={`${c.package_type}:${c.id}`}>
                                              {c.id} — {c.full_name ?? ''}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}
                                  </select>

                                  {availableSharedClients.sameType.length === 0 && availableSharedClients.otherType.length === 0 && sharedClientChips.length === 0 && (
                                    <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '6px' }}>
                                      No hay clientes adicionales disponibles en este manifiesto.
                                    </p>
                                  )}

                                  {sharedClientChips.length > 0 && (
                                    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {sharedClientChips.map(chip => (
                                        <div key={`${chip.package_type}:${chip.client_id}`} style={{
                                          borderLeft: `3px solid ${chip.package_type === 'encomienda' ? '#8b5cf6' : '#00D4B8'}`,
                                          paddingLeft: '8px',
                                          background: chip.package_type === 'encomienda' ? '#f5f3ff' : '#f0fdfb',
                                          borderRadius: '0 4px 4px 0',
                                        }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '6px', paddingBottom: '4px', paddingRight: '4px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '600', flex: 1, color: 'var(--text-main)' }}>
                                              {chip.customerLabel}
                                              <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: chip.package_type === 'encomienda' ? '#7c3aed' : '#0694a2', background: chip.package_type === 'encomienda' ? '#ede9fe' : '#ccfbf1', borderRadius: '3px', padding: '1px 4px' }}>
                                                {chip.package_type === 'encomienda' ? 'ENC' : 'CAS'}
                                              </span>
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => setSharedClientChips(prev => prev.filter(ch => !(ch.client_id === chip.client_id && ch.package_type === chip.package_type)))}
                                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#ef4444' }}
                                            >
                                              <Trash2 size={12} />
                                            </button>
                                          </div>
                                          <div style={{ paddingRight: '8px', paddingBottom: '6px' }}>
                                            <p style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '2px' }}>Peso del grupo (lbs)</p>
                                            <input
                                              type="number"
                                              min="0"
                                              step="0.1"
                                              className="input"
                                              style={{ fontSize: '11px', padding: '3px 6px' }}
                                              placeholder="0.0"
                                              value={(chip.weight_lb ?? 0) === 0 ? '' : chip.weight_lb}
                                              onChange={e => setSharedClientChips(prev => prev.map(ch =>
                                                ch.client_id === chip.client_id && ch.package_type === chip.package_type
                                                  ? { ...ch, weight_lb: parseFloat(e.target.value) || 0 }
                                                  : ch
                                              ))}
                                              onBlur={e => { if (e.target.value === '') setSharedClientChips(prev => prev.map(ch => ch.client_id === chip.client_id && ch.package_type === chip.package_type ? { ...ch, weight_lb: 0 } : ch)); }}
                                            />
                                          </div>
                                          {chip.loadingPackages ? (
                                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', paddingBottom: '6px' }}>Cargando paquetes...</p>
                                          ) : chip.loadError ? (
                                            <p style={{ fontSize: '11px', color: 'var(--avianca-red)', fontStyle: 'italic', paddingBottom: '6px' }}>Error al cargar paquetes — intente de nuevo</p>
                                          ) : chip.packages.length === 0 ? (
                                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', paddingBottom: '6px' }}>Sin paquetes disponibles</p>
                                          ) : (
                                            <div style={{ paddingBottom: '6px' }}>
                                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', fontWeight: '600', padding: '2px 0', cursor: 'pointer' }}>
                                                <input
                                                  type="checkbox"
                                                  checked={chip.packages.length > 0 && chip.selected_package_ids.length === chip.packages.length}
                                                  onChange={e => setSharedClientChips(prev => prev.map(ch =>
                                                    ch.client_id === chip.client_id && ch.package_type === chip.package_type
                                                      ? { ...ch, selected_package_ids: e.target.checked ? ch.packages.map(p => p.id) : [] }
                                                      : ch
                                                  ))}
                                                />
                                                Agregar todos
                                              </label>
                                              {chip.packages.map(pkg => (
                                                <label key={pkg.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', padding: '2px 0', cursor: 'pointer' }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={chip.selected_package_ids.includes(pkg.id)}
                                                    onChange={e => setSharedClientChips(prev => prev.map(ch =>
                                                      ch.client_id === chip.client_id && ch.package_type === chip.package_type
                                                        ? { ...ch, selected_package_ids: e.target.checked ? [...ch.selected_package_ids, pkg.id] : ch.selected_package_ids.filter(id => id !== pkg.id) }
                                                        : ch
                                                    ))}
                                                  />
                                                  <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{pkg.unique_package_id ?? (pkg.package_number != null ? `Paquete ${pkg.package_number}` : pkg.id)}</span>
                                                  {pkg.weight_lb != null && <span style={{ color: 'var(--text-secondary)' }}>{pkg.weight_lb} lb</span>}
                                                </label>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}

                              {sharedClientsInsertError && (
                                <p style={{ fontSize: '11px', color: 'var(--avianca-red)', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <AlertCircle size={11} /> {sharedClientsInsertError}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="pt-2 border-t border-gray-100 space-y-2" style={{ borderColor: 'var(--border)' }}>
                          <p className="metric-label" style={{ fontSize: '10px' }}>Destinatario</p>
                          {existingRecipients.length > 0 && (
                            <select
                              className="input"
                              defaultValue=""
                              onChange={e => {
                                const r = existingRecipients.find(x => x.id === e.target.value);
                                if (r) setBoxRecipientForm({ name: r.full_name || '', phone: r.phone_number || '', address: r.address || '', deliveryType: r.delivery_type || 'Drop Off' });
                              }}
                            >
                              <option value="">— Destinatario existente —</option>
                              {existingRecipients.map(r => (
                                <option key={r.id} value={r.id}>{r.full_name}{r.address ? ' — ' + r.address : ''}</option>
                              ))}
                            </select>
                          )}
                          {savedAddresses.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              <label className="metric-label mb-1 block">Dirección guardada</label>
                              <select
                                className="input"
                                value={selectedAddressId}
                                onChange={e => {
                                  const addr = savedAddresses.find(a => a.id === e.target.value);
                                  if (addr) { setSelectedAddressId(addr.id); applyAddress(addr); }
                                }}
                              >
                                {savedAddresses.map(a => (
                                  <option key={a.id} value={a.id}>
                                    {a.is_default ? '⭐ ' : ''}{a.label} — {a.recipient_name}
                                  </option>
                                ))}
                              </select>
                              <p className="metric-sub mt-1" style={{ color: 'var(--blue-text)' }}>
                                Podés editar los campos abajo sin modificar la dirección guardada.
                              </p>
                            </div>
                          )}
                          {savedAddresses.length === 0 && (boxRecipientForm._customerId || idPrefix) && (
                            <p className="metric-sub mb-2" style={{ color: 'var(--text-tertiary)' }}>
                              Este cliente no tiene direcciones guardadas.
                            </p>
                          )}
                          <input className="input" placeholder="Nombre del destinatario" value={boxRecipientForm.name} onChange={e => setBoxRecipientForm({...boxRecipientForm, name: e.target.value})} />
                          <div className="grid grid-cols-2 gap-2">
                            <input className="input" placeholder="Teléfono" value={boxRecipientForm.phone} onChange={e => setBoxRecipientForm({...boxRecipientForm, phone: e.target.value})} />
                            <input className="input" placeholder="Dirección" value={boxRecipientForm.address} onChange={e => setBoxRecipientForm({...boxRecipientForm, address: e.target.value})} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              className="input"
                              value={boxForm.departamento}
                              onChange={e => setBoxForm({ ...boxForm, departamento: e.target.value, municipio: '' })}
                            >
                              <option value="">-- Departamento --</option>
                              {SV_DEPARTAMENTOS.map(dep => (
                                <option key={dep.name} value={dep.name}>{dep.name}</option>
                              ))}
                            </select>
                            <select
                              className="input"
                              value={boxForm.municipio}
                              disabled={!boxForm.departamento}
                              onChange={e => setBoxForm({ ...boxForm, municipio: e.target.value })}
                            >
                              <option value="">-- Municipio --</option>
                              {(SV_DEPARTAMENTOS.find(d => d.name === boxForm.departamento)?.municipios || []).map(mun => (
                                <option key={mun.name} value={mun.name}>{mun.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="metric-label" style={{ fontSize: '10px' }}>Punto de Referencia</label>
                            <input className="input" placeholder="Ej: Contiguo a farmacia, casa de portón azul" value={boxForm.reference_point ?? ''} onChange={e => setBoxForm({...boxForm, reference_point: e.target.value})} />
                          </div>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setBoxRecipientForm({...boxRecipientForm, deliveryType: 'Pick Up'})} className={`btn ${boxRecipientForm.deliveryType === 'Pick Up' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, justifyContent: 'center' }}>📦 Pick Up</button>
                            <button type="button" onClick={() => setBoxRecipientForm({...boxRecipientForm, deliveryType: 'Drop Off'})} className={`btn ${boxRecipientForm.deliveryType === 'Drop Off' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, justifyContent: 'center', background: boxRecipientForm.deliveryType === 'Drop Off' ? 'var(--green)' : '' }}>🚚 Drop Off</button>
                          </div>
                          {(boxRecipientForm.deliveryType || '') === 'Drop Off' && (boxForm.departamento || '') && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'var(--blue-soft)',
                              borderRadius: 'var(--radius-sm)',
                              padding: '6px 10px',
                              fontSize: '11px',
                              color: 'var(--blue-text)',
                              fontWeight: '600',
                            }}>
                              <span>🔒</span>
                              <span>Esta caja se guardará en <strong>Hold</strong> — liberala desde el manifiesto cuando esté lista para envío.</span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleSaveBox(boxRecipientForm._customerId || idPrefix)} disabled={boxSaving || (primaryPackagesFetched && !loadingPrimaryPackages && primaryPackages.length === 0)} className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                            {boxSaving ? 'Guardando...' : 'Guardar Caja'}
                          </button>
                          <button type="button" onClick={() => { setCreatingBoxFor(null); setBoxForm({ weight: '', dims: '', comments: '', municipio: '', departamento: '', reference_point: '', boxful_state_id: '', boxful_city_id: '' }); setBoxRecipientForm({ name: '', phone: '', address: '', deliveryType: 'Drop Off', _customerId: '' }); setSavedAddresses([]); setSelectedAddressId(''); setSharedClientChips([]); setSharedClientsOpen(false); setAvailableSharedClients([]); setSharedClientsInsertError(''); setPrimaryPackages([]); setSelectedPrimaryPackageIds([]); setPrimaryPackagesFetched(false); }} className="btn btn-ghost">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit encomienda overlay */}
      {editingEncomiendaId && (
        <div className="fixed inset-0 bg-black/60 z-40 flex flex-col overflow-y-auto">
          <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
            <button
              onClick={() => { setEditingEncomiendaId(null); onRefreshEncomiendas(); }}
              className="flex items-center gap-2 text-gray-600 font-bold text-sm hover:text-gray-900 transition"
            >
              ← Volver al Manifiesto
            </button>
            <span className="text-xs font-bold text-orange-600 uppercase tracking-wide">
              Editar Encomienda
            </span>
          </div>
          <div className="flex-1 p-4 bg-gray-50">
            <EncomiendaWizard
              currentManifest={currentManifest}
              user={user}
              editMode={true}
              encomiendaId={editingEncomiendaId}
              onComplete={() => { setEditingEncomiendaId(null); onRefreshEncomiendas(); }}
              onClose={() => { setEditingEncomiendaId(null); onRefreshEncomiendas(); }}
            />
          </div>
        </div>
      )}

      {/* AI Receipt Analyzer — casillero shipments (staff only) */}
      {analyzingShipmentId !== null && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setAnalyzingShipmentId(null); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
          }}
        >
          <div style={{
            background: '#ffffff', borderRadius: '16px', padding: '24px',
            width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontWeight: '500', fontSize: '15px' }}>Análisis de Recibo — Casillero</span>
              <button
                onClick={() => setAnalyzingShipmentId(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
              >×</button>
            </div>
            <ReceiptAnalyzer
              packageId={analyzingShipmentId}
              encomiendaId={null}
              existingItems={[]}
              onItemsConfirmed={async (items) => {
                try {
                  const inserts = items.map(item => ({
                    shipment_id: analyzingShipmentId,
                    description: item.description || '',
                    quantity: parseFloat(item.quantity) || 1,
                    unit_value: parseFloat(item.unit_price) || 0,
                    condition: 'NEW',
                    is_merchandise: true,
                    pending_review: false,
                  }));
                  const { error } = await supabase.from('shipment_items').insert(inserts);
                  if (error) throw error;
                  console.log('[Manifest AI] Saved:', inserts.length, 'items for shipment', analyzingShipmentId);
                  setAnalyzingShipmentId(null);
                } catch (err) {
                  console.error('[Manifest AI] Error:', err);
                  alert('Error guardando artículos: ' + err.message);
                }
              }}
              onClose={() => setAnalyzingShipmentId(null)}
            />
          </div>
        </div>
      )}

      {/* ── Editar Paquetes Modal ── */}
      {editingPkgsBoxId !== null && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setEditingPkgsBoxId(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}
        >
          <div style={{ background: '#fff', maxWidth: '480px', width: '100%', marginTop: '40px', borderRadius: '12px', padding: '20px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-main)' }}>
                Editar Paquetes — Caja {editingPkgsBoxId}
              </span>
              <button
                type="button"
                onClick={() => setEditingPkgsBoxId(null)}
                style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {editPkgsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                Cargando...
              </div>
            ) : (
              <>
                {editPkgsData.allGroups.map((group, gi) => (
                  <div key={group.client_id} style={{ marginBottom: '16px', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', borderLeft: group.is_primary ? '3px solid var(--blue)' : '3px solid #00D4B8' }}>
                    {/* Group header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '700', fontSize: '12px', color: 'var(--text-main)' }}>
                        {group.client_id} — {group.full_name}
                      </span>
                      <span style={{
                        fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px',
                        background: group.is_primary ? 'var(--blue-soft)' : '#e6faf8',
                        color: group.is_primary ? 'var(--blue-text)' : '#00897b',
                      }}>
                        {group.is_primary ? 'Principal' : 'Compartido'}
                      </span>
                    </div>

                    {/* Weight input */}
                    <div style={{ marginBottom: '8px' }}>
                      <p style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Peso del grupo (lbs)</p>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="0.0"
                        className="input"
                        style={{ width: '120px' }}
                        value={(group.weight_lb ?? 0) === 0 ? '' : group.weight_lb}
                        onChange={e => setEditPkgsData(prev => ({
                          ...prev,
                          allGroups: prev.allGroups.map((g, i) => i === gi ? { ...g, weight_lb: parseFloat(e.target.value) || 0 } : g),
                        }))}
                        onBlur={e => { if (e.target.value === '') setEditPkgsData(prev => ({ ...prev, allGroups: prev.allGroups.map((g, i) => i === gi ? { ...g, weight_lb: 0 } : g) })); }}
                      />
                    </div>

                    {/* Package checklist */}
                    {group.packages.length > 0 ? (
                      <div style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                          <input
                            type="checkbox"
                            checked={group.packages.filter(p => !p.lockedInOtherBox).length > 0 && group.packages.filter(p => !p.lockedInOtherBox).every(p => p.checked)}
                            onChange={e => setEditPkgsData(prev => ({
                              ...prev,
                              allGroups: prev.allGroups.map((g, i) => i === gi
                                ? { ...g, packages: g.packages.map(p => p.lockedInOtherBox ? p : { ...p, checked: e.target.checked }) }
                                : g),
                            }))}
                          />
                          Agregar todos
                        </label>
                        {group.packages.map((pkg, pi) => (
                          <label
                            key={pkg.id}
                            title={pkg.lockedInOtherBox ? 'En otra caja — reasignar primero' : undefined}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', fontSize: '11px', cursor: pkg.lockedInOtherBox ? 'not-allowed' : 'pointer', borderBottom: '1px solid var(--border)', opacity: pkg.lockedInOtherBox ? 0.45 : 1 }}
                          >
                            <input
                              type="checkbox"
                              checked={pkg.checked}
                              disabled={pkg.lockedInOtherBox}
                              onChange={e => setEditPkgsData(prev => ({
                                ...prev,
                                allGroups: prev.allGroups.map((g, i) => i === gi
                                  ? { ...g, packages: g.packages.map((p, j) => j === pi ? { ...p, checked: e.target.checked } : p) }
                                  : g),
                              }))}
                            />
                            <span style={{ fontFamily: 'monospace', fontWeight: '600', flex: 1 }}>{pkg.label}</span>
                            {pkg.lockedInOtherBox && <span style={{ fontSize: '10px', color: '#c2410c' }}>🔒</span>}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Sin paquetes disponibles en este manifiesto.</p>
                    )}
                  </div>
                ))}

                {editPkgsData.allGroups.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic', padding: '20px 0' }}>
                    Sin grupos asignados a esta caja.
                  </p>
                )}

                {editPkgsData.saveError && (
                  <p style={{ fontSize: '12px', color: 'var(--avianca-red)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ⚠ {editPkgsData.saveError}
                  </p>
                )}

                {/* Footer */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button
                    type="button"
                    onClick={handleSaveEditPkgs}
                    disabled={editPkgsData.saving}
                    className="btn btn-primary"
                    style={{ flex: 1, justifyContent: 'center', opacity: editPkgsData.saving ? 0.7 : 1 }}
                  >
                    {editPkgsData.saving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingPkgsBoxId(null)}
                    className="btn btn-ghost"
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {courierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-base">Seleccionar Mensajero</h3>
              <button onClick={() => setCourierModal(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            {courierModal.success ? (
              <p className="text-green-600 font-medium text-sm text-center py-4">{courierModal.success}</p>
            ) : (
              <>
                {courierModal.error && (
                  <p className="text-red-500 text-xs mb-3 p-2 bg-red-50 rounded">{courierModal.error}</p>
                )}
                {courierModal.couriers === null ? (
                  <p className="text-gray-400 text-sm italic text-center py-6">Obteniendo cotizaciones...</p>
                ) : courierModal.couriers.length === 0 ? (
                  <p className="text-gray-400 text-sm italic text-center py-6">Sin mensajeros disponibles para esta ruta.</p>
                ) : (
                  <div className="space-y-2">
                    {courierModal.couriers.map(c => (
                      <button
                        key={c.id}
                        disabled={courierModal.dispatching}
                        onClick={() => handleSelectCourier(c.id)}
                        className="w-full flex justify-between items-center p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition disabled:opacity-50"
                      >
                        <span className="font-medium text-sm">{c.name}</span>
                        <span className="text-sm font-bold text-green-700">${Number(c.clientPrice).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {courierModal.dispatching && (
                  <p className="text-blue-500 text-sm italic text-center mt-3">Despachando...</p>
                )}
              </>
            )}
            <button
              onClick={() => setCourierModal(null)}
              className="btn btn-ghost w-full mt-4"
            >
              {courierModal.success ? 'Cerrar' : 'Cancelar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Live Manifest (main component) ───────────────────────────────────────────
const LiveManifest = ({
  shipments, boxes, onUpdate, onDeleteShipment, onAddBox, onUpdateBox, onDeleteBox, t,
  currentManifest, liveManifest, onCloseManifest, user, userPermissions, onSessionManifestChange, activeSessionManifest,
  onReopenLastManifest,
}) => {
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeSuccessMsg, setCloseSuccessMsg] = useState('');

  // FIX 3: completed encomiendas for this manifest
  const [encomiendas, setEncomiendas] = useState([]);

  // Open manifest costeo preview
  const [showOpenManifestPreview, setShowOpenManifestPreview] = useState(false);
  const [openManifestCosteoData, setOpenManifestCosteoData] = useState(null);
  const [openManifestLoading, setOpenManifestLoading] = useState(false);

  // Close requests (bodega → manager notification)
  const [closeRequests, setCloseRequests] = useState([]);

  // Session manifest (reopened past manifest) — activeSessionManifest is owned by App.js
  const [sessionManifestShipments, setSessionManifestShipments] = useState([]);
  const [sessionManifestEncomiendas, setSessionManifestEncomiendas] = useState([]);
  const [sessionManifestBoxes, setSessionManifestBoxes] = useState([]);

  const [activeTab, setActiveTab] = useState('active');
  const { tenant } = useTenant();

  /* eslint-disable */
  useEffect(() => {
    if (activeSessionManifest) {
      setActiveTab('session');
    } else {
      setActiveTab('active');
    }
  }, [activeSessionManifest]);
  /* eslint-enable */

  const fetchCloseRequests = async () => {
    if (!userPermissions?.canCloseManifest) return;
    let crQuery = supabase
      .from('manifest_close_requests')
      .select('*')
      .eq('status', 'pending')
      .order('requested_at', { ascending: false });
    const { data } = await crQuery;
    setCloseRequests(data || []);
  };

  const handleRequestClose = async () => {
    if (!currentManifest?.id) return;
    try {
      const { error } = await supabase
        .from('manifest_close_requests')
        .insert({
          manifest_id: currentManifest.id,
          requested_by: user.id,
          requested_at: new Date().toISOString(),
          status: 'pending',
        });
      if (error) throw error;
      alert('Solicitud de cierre enviada al manager.');
    } catch (err) {
      console.error('[RequestClose]', err);
      alert('Error al enviar solicitud: ' + err.message);
    }
  };

  const handleToggleOpenPreview = async () => {
    const nowShowing = !showOpenManifestPreview;
    setShowOpenManifestPreview(nowShowing);
    // Use activeSessionManifest when in session tab, otherwise currentManifest
    const targetManifest = activeSessionManifest || liveManifest || currentManifest;
    if (nowShowing && !openManifestCosteoData && targetManifest) {
      setOpenManifestLoading(true);
      try {
        const data = await buildCosteoData(targetManifest);
        if (!data) {
          console.warn('[OpenPreview] buildCosteoData returned null for:', targetManifest?.id);
        }
        setOpenManifestCosteoData(data);
      } catch (e) {
        console.error('[OpenPreview] Error:', e);
      } finally {
        setOpenManifestLoading(false);
      }
    }
  };

  const fetchEncomiendas = async () => {
    const activeManifest = currentManifest || liveManifest;
    if (!activeManifest?.id) {
      console.log('[Encomiendas] No manifest ID — skipping fetch');
      return;
    }

    console.log('[Encomiendas] Current manifest ID:', activeManifest.id);
    console.log('[Encomiendas] Status filter: COMPLETE');

    let encQuery = supabase
      .from('encomiendas')
      .select(`
        *,
        customers(full_name, phone_number),
        encomienda_packages(id, recipient_name, package_number, unique_package_id, delivery_type, total_fob, receipt_url, photo_url, staff_comments),
        encomienda_boxes(id, box_number, weight_lb, dimensions)
      `)
      .eq('manifest_id', activeManifest.id)
      .eq('status', 'COMPLETE')
      .order('created_at', { ascending: false });
    const { data: encomiendaData, error: encomiendaError } = await encQuery;

    console.log('[Encomiendas] Query result data:', encomiendaData);
    console.log('[Encomiendas] Query result error:', encomiendaError);

    // Diagnostic: check all recent encomiendas regardless of filter
    let diagQuery = supabase
      .from('encomiendas')
      .select('id, unique_encomienda_id, status, manifest_id')
      .order('created_at', { ascending: false })
      .limit(10);
    const { data: allEnc } = await diagQuery;
    console.log('[Encomiendas] All recent encomiendas (last 10):', allEnc);

    // Explicit encomienda_boxes fetch — supplements nested select which may fail silently
    if (encomiendaData && encomiendaData.length > 0) {
      const encIds = encomiendaData.map(e => e.id);
      const { data: allEncBoxes } = await supabase
        .from('encomienda_boxes')
        .select('id, box_number, weight_lb, encomienda_id, dimensions')
        .in('encomienda_id', encIds);
      if (allEncBoxes && allEncBoxes.length > 0) {
        encomiendaData.forEach(enc => {
          enc.encomienda_boxes = allEncBoxes.filter(b => b.encomienda_id === enc.id);
        });
        console.log('[Encomiendas] Explicit encBoxes fetched:', allEncBoxes.length);
      }
    }

    setEncomiendas(encomiendaData || []);

    // FIX 4 — log grouping info so we can check customer_id alignment
    console.log('[OpenManifest] Encomiendas fetched:', (encomiendaData || []).map(e => ({
      id: e.unique_encomienda_id,
      customer: e.customer_id,
      manifest: e.manifest_id,
    })));
  };

  /* eslint-disable */
  useEffect(() => {
    setOpenManifestCosteoData(null);
    setShowOpenManifestPreview(false);
    if (!tenant) return;
    fetchEncomiendas();
    fetchCloseRequests();
  }, [tenant, currentManifest?.id]);
  /* eslint-enable */

  const handleCloseConfirm = async (pin) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: 'Sesión expirada. Recarga la página.' };
    }
    const result = await onCloseManifest(pin);
    if (result.success) {
      setShowCloseModal(false);
      setCloseSuccessMsg('Manifiesto cerrado. Nuevo manifiesto: ' + result.newManifestId);
      setTimeout(() => setCloseSuccessMsg(''), 8000);
    }
    return result;
  };

  const handleReopenManifest = async (manifest) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      alert('Sesión expirada. Recarga la página.');
      return;
    }

    if (currentManifest?.id && manifest.id === currentManifest.id) {
      alert('Este manifiesto ya está abierto como el manifiesto activo.');
      return;
    }

    if (activeSessionManifest?.id === manifest.id) {
      await closeManifestSession(supabase, manifest.id, user);
      onSessionManifestChange(null);
      setSessionManifestShipments([]);
      setSessionManifestEncomiendas([]);
      setSessionManifestBoxes([]);
      return;
    }

    if (activeSessionManifest) {
      await closeManifestSession(supabase, activeSessionManifest.id, user);
    }

    await openManifestSession(supabase, manifest.id, user);
    onSessionManifestChange({ ...manifest, status: 'OPEN' });

    const [{ data: shipData }, { data: encData }, { data: boxData }] =
      await Promise.all([
        supabase
          .from('shipments')
          .select('*, customers(id, full_name)')
          .eq('manifest_id', manifest.id)
          .order('entry_timestamp', { ascending: false }),
        supabase
          .from('encomiendas')
          .select(`
            *,
            customers(full_name, phone_number),
            encomienda_packages(
              id, recipient_name, package_number,
              unique_package_id, delivery_type,
              total_fob, receipt_url, photo_url, staff_comments
            ),
            encomienda_boxes(id, box_number, weight_lb, dimensions)
          `)
          .eq('manifest_id', manifest.id)
          .eq('status', 'COMPLETE')
          .order('created_at', { ascending: false }),
        supabase
          .from('boxes')
          .select('*, customers(id, full_name)')
          .eq('manifest_id', manifest.id)
          .order('created_at', { ascending: true }),
      ]);

    setSessionManifestShipments(shipData || []);
    setSessionManifestEncomiendas(encData || []);
    setSessionManifestBoxes(boxData || []);

    console.log('[ReopenManifest] Opened:', manifest.id,
      'shipments:', (shipData || []).length,
      'encomiendas:', (encData || []).length);
  };

  const closeSession = async () => {
    if (!activeSessionManifest) return;
    await closeManifestSession(supabase, activeSessionManifest.id, user);
    onSessionManifestChange(null);
    setSessionManifestShipments([]);
    setSessionManifestEncomiendas([]);
    setSessionManifestBoxes([]);
  };

  return (
    <div>
      {/* ── Tab bar — only shown when a session manifest is active ── */}
      {activeSessionManifest && (
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '16px',
          borderBottom: '2px solid var(--border)',
          paddingBottom: '0',
        }}>
          <button
            onClick={() => setActiveTab('active')}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              borderBottom: activeTab === 'active'
                ? '3px solid var(--sky-blue)'
                : '3px solid transparent',
              background: 'transparent',
              color: activeTab === 'active' ? 'var(--sky-blue)' : 'var(--text-secondary)',
              cursor: 'pointer',
              marginBottom: '-2px',
            }}
          >
            📦 {(liveManifest || currentManifest)?.id}
            <span style={{
              marginLeft: '6px',
              fontSize: '10px',
              background: 'var(--blue-soft)',
              color: 'var(--blue-text)',
              borderRadius: '999px',
              padding: '1px 7px',
              fontWeight: '700',
            }}>
              ACTIVO
            </span>
          </button>
          <button
            onClick={() => setActiveTab('session')}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              borderBottom: activeTab === 'session'
                ? '3px solid var(--avianca-red)'
                : '3px solid transparent',
              background: 'transparent',
              color: activeTab === 'session' ? 'var(--avianca-red)' : 'var(--text-secondary)',
              cursor: 'pointer',
              marginBottom: '-2px',
            }}
          >
            🗂 {activeSessionManifest.id}
            <span style={{
              marginLeft: '6px',
              fontSize: '10px',
              background: '#fef2f2',
              color: 'var(--avianca-red)',
              borderRadius: '999px',
              padding: '1px 7px',
              fontWeight: '700',
            }}>
              SESIÓN
            </span>
          </button>
        </div>
      )}

      {/* ── ACTIVE MANIFEST TAB ── */}
      {(!activeSessionManifest || activeTab === 'active') && (
        <div>
          {/* Pending close requests banner — visible to admin/manager only */}
          {closeRequests.length > 0 && userPermissions?.canCloseManifest && (
            <div style={{
              background: 'var(--amber-soft)',
              border: '1px solid var(--amber)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              fontSize: '12px',
              color: 'var(--amber)',
              fontWeight: '500',
              marginBottom: '12px',
            }}>
              {closeRequests.length} solicitud(es) de cierre pendiente(s) — revise el manifiesto
            </div>
          )}

          <ManifestStatusBar
            currentManifest={liveManifest || currentManifest}
            shipments={shipments}
            encomiendas={encomiendas}
            boxes={boxes}
            onOpenCloseModal={() => setShowCloseModal(true)}
            showOpenManifestPreview={showOpenManifestPreview}
            onToggleCosteoPreview={handleToggleOpenPreview}
            userPermissions={userPermissions}
            onRequestClose={handleRequestClose}
          />

          {/* Success banners */}
          {closeSuccessMsg && (
            <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 text-sm font-medium px-4 py-3 rounded-xl">
              <span className="text-green-500">✓</span> {closeSuccessMsg}
            </div>
          )}

          <LiveManifestBody
            shipments={shipments}
            boxes={boxes}
            encomiendas={encomiendas}
            currentManifest={liveManifest || currentManifest}
            onUpdate={onUpdate}
            onDeleteShipment={onDeleteShipment}
            onAddBox={onAddBox}
            onUpdateBox={onUpdateBox}
            onDeleteBox={onDeleteBox}
            onEncomiendaDeleted={(id) => setEncomiendas(prev => prev.filter(e => e.id !== id))}
            onRefreshEncomiendas={fetchEncomiendas}
            user={user}
            userPermissions={userPermissions}
            t={t}
            isSessionView={false}
          />

          {/* Open manifest costeo preview panel */}
          {showOpenManifestPreview && (
            <div style={{
              margin: '16px 0',
              padding: '16px',
              background: '#ffffff',
              border: '0.5px solid #e5e7eb',
              borderRadius: '12px',
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '12px',
              }}>
                Vista previa del costeo — {currentManifest?.id}
              </div>
              {openManifestLoading ? (
                <p style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: '#6b7280' }}>
                  Cargando costeo...
                </p>
              ) : openManifestCosteoData ? (
                <CosteoPreview
                  manifest={openManifestCosteoData}
                  packages={openManifestCosteoData.packages}
                  onExport={userPermissions?.canExportCosteo ? () => generateCosteoXLSX(openManifestCosteoData) : null}
                />
              ) : (
                <p style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                  Sin datos disponibles. Asegúrese de tener paquetes en el manifiesto.
                </p>
              )}
            </div>
          )}

          {/* Manifest history */}
          <ManifestHistory
            onReopenManifest={handleReopenManifest}
            tenant={tenant}
            user={user}
            userPermissions={userPermissions}
          />
        </div>
      )}

      {/* ── SESSION MANIFEST TAB ── */}
      {activeSessionManifest && activeTab === 'session' && (
        <div>
          <ManifestStatusBar
            currentManifest={activeSessionManifest}
            shipments={sessionManifestShipments}
            encomiendas={sessionManifestEncomiendas}
            boxes={sessionManifestBoxes}
            onOpenCloseModal={() => {}}
            showOpenManifestPreview={false}
            onToggleCosteoPreview={() => {}}
            userPermissions={{ ...userPermissions, canCloseManifest: false, canRequestClose: false }}
            onRequestClose={() => {}}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
            {user?.role === 'admin' && onReopenLastManifest && (
              <button
                type="button"
                onClick={async () => {
                  const confirmed = window.confirm(
                    '⚠️ ADMIN: ¿Revertir cierre del último manifiesto?\n\nEsto cerrará el manifiesto activo actual (debe estar vacío) y reabrirá el más reciente cerrado como el nuevo manifiesto activo.'
                  );
                  if (!confirmed) return;
                  const result = await onReopenLastManifest();
                  if (result?.success) {
                    alert(`✅ Manifiesto ${result.reopenedId} restaurado como activo.`);
                  } else if (result?.error) {
                    alert('❌ ' + result.error);
                  }
                }}
                className="btn btn-secondary"
                style={{ color: 'var(--avianca-red)', borderColor: 'var(--avianca-red)', fontSize: '12px' }}
              >
                ⚠️ Revertir Cierre
              </button>
            )}
            <button
              onClick={closeSession}
              style={{
                fontSize: '12px',
                fontWeight: '600',
                padding: '6px 16px',
                border: '1px solid var(--avianca-red)',
                borderRadius: '20px',
                background: 'transparent',
                color: 'var(--avianca-red)',
                cursor: 'pointer',
              }}
            >
              ✕ Cerrar Sesión
            </button>
          </div>

          <LiveManifestBody
            shipments={sessionManifestShipments}
            boxes={sessionManifestBoxes}
            encomiendas={sessionManifestEncomiendas}
            currentManifest={activeSessionManifest}
            onUpdate={async (id, data) => {
              await supabase.from('shipments').update(data).eq('id', id);
              setSessionManifestShipments(prev =>
                prev.map(s => s.id === id ? { ...s, ...data } : s)
              );
            }}
            onDeleteShipment={(id) =>
              setSessionManifestShipments(prev =>
                prev.filter(s => s.id !== id)
              )
            }
            onAddBox={(box) =>
              setSessionManifestBoxes(prev => [...prev, box])
            }
            onUpdateBox={async (boxId, data) => {
              await supabase.from('boxes').update(data).eq('box_id', boxId);
              setSessionManifestBoxes(prev =>
                prev.map(b => b.box_id === boxId ? { ...b, ...data } : b)
              );
            }}
            // UI state only — the DELETE runs in handleDeleteBoxGuarded after
            // the billing guard, so it must not be repeated here.
            onDeleteBox={(boxId) =>
              setSessionManifestBoxes(prev =>
                prev.filter(b => b.box_id !== boxId)
              )
            }
            onEncomiendaDeleted={(id) =>
              setSessionManifestEncomiendas(prev =>
                prev.filter(e => e.id !== id)
              )
            }
            onRefreshEncomiendas={async () => {
              const { data } = await supabase
                .from('encomiendas')
                .select(`*, customers(full_name, phone_number), encomienda_packages(id, recipient_name, package_number, unique_package_id, delivery_type, total_fob, receipt_url, photo_url, staff_comments), encomienda_boxes(id, box_number, weight_lb, dimensions)`)
                .eq('manifest_id', activeSessionManifest.id)
                .eq('status', 'COMPLETE')
                .order('created_at', { ascending: false });
              setSessionManifestEncomiendas(data || []);
            }}
            user={user}
            userPermissions={userPermissions}
            t={t}
            isSessionView={true}
          />
        </div>
      )}

      {/* Close manifest modal — always present regardless of active tab */}
      {showCloseModal && (
        <CloseManifestModal
          manifest={currentManifest}
          shipments={shipments}
          userRole={user?.role}
          onCancel={() => setShowCloseModal(false)}
          onConfirm={handleCloseConfirm}
        />
      )}
    </div>
  );
};

// ── Delete Package Modal ─────────────────────────────────────────────────────
const DeletePackageModal = ({ item, user, onCancel, onConfirm }) => {
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const locked = attempts >= 3;
  const isAdmin = user?.role === 'admin';

  const handleConfirm = async () => {
    if (locked) return;
    setDeleting(true);
    setPinError('');
    const result = await onConfirm(isAdmin ? null : pin);
    setDeleting(false);
    if (!result.success) {
      const next = attempts + 1;
      setAttempts(next);
      setPinError(next >= 3
        ? 'Demasiados intentos. Contacte al administrador.'
        : result.error || 'Error al eliminar.'
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="font-bold text-gray-900 text-lg mb-1">
          ¿Eliminar paquete {item.unique_package_id}?
        </h3>
        <p className="text-sm text-gray-500 mb-5">Esta acción no se puede deshacer.</p>

        {!isAdmin && !locked && (
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
              Ingrese su PIN para confirmar eliminación
            </label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={e => { setPin(e.target.value); setPinError(''); }}
              placeholder="• • • •"
              className="w-full text-center text-2xl tracking-[0.5em] font-mono p-3 border border-gray-300 rounded-xl outline-none focus:border-red-400"
            />
          </div>
        )}

        {pinError && (
          <p className="text-red-500 text-xs mb-4 flex items-center gap-1">
            <AlertCircle size={12} /> {pinError}
          </p>
        )}

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 py-2.5 border rounded-xl text-gray-600 text-sm font-bold hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting || locked || (!isAdmin && pin.length === 0)}
            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition flex items-center justify-center gap-1"
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Shipment Items List ───────────────────────────────────────────────────────
const ShipmentItemsList = ({ shipmentId }) => {
  const [items, setItems] = useState([]);
  const { tenant } = useTenant();

  /* eslint-disable */
  useEffect(() => {
    if (!tenant) return;
    if (!shipmentId) return;
    supabase
      .from('shipment_items')
      .select('*')
      .eq('shipment_id', shipmentId)
      .then(({ data }) => setItems(data || []));
  }, [tenant, shipmentId]);
  /* eslint-enable */

  if (items.length === 0) return null;

  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{
        fontSize: '11px', fontWeight: '500',
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px'
      }}>
        {items.length} artículo(s) registrado(s)
      </div>
      {items.map(item => (
        <div key={item.id} style={{
          fontSize: '12px', color: 'var(--color-text-secondary)',
          padding: '3px 0', display: 'flex', justifyContent: 'space-between'
        }}>
          <span>{item.quantity}× {item.description}</span>
          <span style={{ color: 'var(--color-text-success)', fontWeight: '500' }}>
            ${parseFloat(item.unit_value).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Package Receipts Panel ────────────────────────────────────────────────────
const PackageReceiptsPanel = ({ shipment, manifestId, uploadedBy }) => {
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const { tenant } = useTenant();

  const fetchReceipts = async () => {
    const { data } = await supabase
      .from('package_receipts')
      .select('*')
      .eq('shipment_id', shipment.id)
      .order('uploaded_at', { ascending: false });
    setReceipts(data || []);
  };

  /* eslint-disable */
  useEffect(() => { if (!tenant) return; fetchReceipts(); }, [tenant, shipment.id]);
  /* eslint-enable */

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 20 - receipts.length;
    if (remaining <= 0) {
      setError('Máximo 20 recibos por paquete.');
      return;
    }

    const toUpload = files.slice(0, remaining);
    setUploading(true);
    setError('');

    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Archivo muy grande. Máximo 5MB por archivo.');
        continue;
      }
      try {
        const ext = file.name.split('.').pop();
        const filePath = `receipts/${manifestId}/${shipment.unique_package_id}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('package-photos')
          .upload(filePath, file);
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage
          .from('package-photos')
          .getPublicUrl(filePath);
        await supabase.from('package_receipts').insert({
          shipment_id: shipment.id,
          receipt_url: urlData.publicUrl,
          uploaded_by: uploadedBy,
          source: 'staff',
          manifest_id: manifestId,
        });
        console.log('[PackageReceipts] Uploaded to:', filePath, 'shipment:', shipment.unique_package_id);
      } catch (err) {
        setError('Error subiendo archivo: ' + err.message);
      }
    }

    await fetchReceipts();
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (receiptId) => {
    if (!window.confirm('¿Eliminar este recibo?')) return;
    await supabase
      .from('package_receipts')
      .delete()
      .eq('id', receiptId);
    await fetchReceipts();
  };

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{
        fontSize: '11px',
        fontWeight: '500',
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>Recibos ({receipts.length}/20)</span>
        {receipts.length < 20 && (
          <label style={{
            fontSize: '11px',
            fontWeight: '500',
            padding: '4px 10px',
            border: '1px solid var(--blue)',
            borderRadius: '8px',
            color: 'var(--blue-text)',
            cursor: 'pointer',
            background: 'var(--blue-soft)',
          }}>
            {uploading ? 'Subiendo...' : '+ Agregar'}
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,application/pdf"
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        )}
      </div>

      {error && (
        <p style={{
          fontSize: '11px',
          color: 'var(--avianca-red)',
          marginBottom: '6px',
        }}>
          {error}
        </p>
      )}

      {receipts.length === 0 ? (
        <div style={{
          border: '1px dashed var(--color-border-tertiary)',
          borderRadius: 'var(--border-radius-md)',
          padding: '16px',
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--color-text-tertiary)',
          fontStyle: 'italic',
        }}>
          Sin recibos adjuntos
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: '6px',
        }}>
          {receipts.map(r => (
            <div key={r.id} style={{
              position: 'relative',
              border: '1px solid var(--color-border-tertiary)',
              borderRadius: '6px',
              overflow: 'hidden',
              aspectRatio: '1',
              background: 'var(--color-background-secondary)',
            }}>
              <a
                href={r.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  fontSize: '10px',
                  color: 'var(--color-text-secondary)',
                  textDecoration: 'none',
                }}
              >
                {r.receipt_url.includes('.pdf')
                  ? '📄 PDF'
                  : <img
                      src={r.receipt_url}
                      alt="receipt"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                }
              </a>
              <button
                onClick={() => handleDelete(r.id)}
                style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  background: 'rgba(0,0,0,0.5)',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '10px',
                  cursor: 'pointer',
                  padding: '1px 4px',
                  lineHeight: '1.4',
                }}
              >
                ×
              </button>
              <div style={{
                position: 'absolute',
                bottom: '2px',
                left: '2px',
                fontSize: '8px',
                color: '#fff',
                background: 'rgba(0,0,0,0.4)',
                borderRadius: '3px',
                padding: '1px 3px',
              }}>
                {r.source === 'client' ? 'Cliente' : r.uploaded_by}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Manifest Row ─────────────────────────────────────────────────────────────
const ManifestRow = ({ item, onUpdate, onDeleteShipment, handleMerge, user, userPermissions, onDeleteSuccess, setAnalyzingShipmentId }) => {
  const { tenant } = useTenant();
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scanningTracking, setScanningTracking] = useState(false);
  const [scanTrackingError, setScanTrackingError] = useState(null);

  const isCollision = (item.unique_package_id || '').endsWith('-DUP');

  const startEdit = () => {
    setEditForm({ ...item });
    setIsEditing(true);
  };

  const saveEdit = async () => {
    const { id, customers: _customers, ...updateData } = editForm;
    await onUpdate(id, { ...updateData, updated_at: new Date().toISOString() });
    setIsEditing(false);
  };

  const handleScanTracking = async () => {
    const photoUrl = item.warehouse_photo_url || item.photo_url;
    if (!photoUrl) return;
    setScanTrackingError(null);
    setScanningTracking(true);
    try {
      const result = await scanBarcode(photoUrl);
      if (result.success) {
        await onUpdate(item.id, {
          tracking_number: result.tracking_number,
          updated_at: new Date().toISOString(),
        });
        console.log('[ManifestRow AI Scan] Saved:', result.tracking_number);
      } else {
        setScanTrackingError(result.error);
      }
    } catch (err) {
      console.error('[ManifestRow AI Scan] Error:', err);
      setScanTrackingError('Error al escanear — intente de nuevo');
    } finally {
      setScanningTracking(false);
    }
  };

  const triggerPhotoUpload = (shipmentId) => {
    document.getElementById(`photo-upload-${shipmentId}`)?.click();
  };

  const handleManifestPhotoUpload = async (e, shipment) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      alert('Formato no válido. Use JPEG, PNG, o PDF.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Archivo muy grande. Máximo 5MB.');
      return;
    }
    try {
      const fileExt = file.name.split('.').pop();
      // warehouse/ folder — never used by AI analyzer (AI only reads receipts/)
      const filePath = `warehouse/${shipment.manifest_id}/${shipment.unique_package_id}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('package-photos').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(filePath);
      console.log('[PhotoUpload] updating shipment:', shipment.id, 'with url:', urlData.publicUrl);
      const { error: updateError } = await supabase
        .from('shipments')
        .update({ warehouse_photo_url: urlData.publicUrl })
        .eq('id', shipment.id);
      if (updateError) {
        console.error('[PhotoUpload] DB update failed:', updateError);
        alert('Error saving photo: ' + updateError.message);
        return;
      }
      console.log('[PhotoUpload] saved to DB:', urlData.publicUrl);
    } catch (err) {
      console.error('[ManifestPhoto] Error:', err);
      alert('Error al subir archivo: ' + err.message);
    }
    e.target.value = '';
  };

  const handleDeleteConfirm = async (pin) => {
    const currentTenantId = tenant?.id;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: 'Sesión expirada. Recarga la página.' };
    }

    if (pin) {
      const userEmail = session.user.email;
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: pin,
      });
      if (authError) {
        return { success: false, error: 'PIN incorrecto.' };
      }
    }

    const { error: bpErr } = await supabase
      .from('box_packages')
      .delete()
      .eq('tenant_id', currentTenantId)
      .eq('package_type', 'casillero')
      .eq('package_ref_id', item.id);
    if (bpErr) {
      return { success: false, error: `Error eliminando de cajas: ${bpErr.message}` };
    }

    const { error: shipErr } = await supabase
      .from('shipments')
      .delete()
      .eq('tenant_id', currentTenantId)
      .eq('id', item.id);
    if (shipErr) {
      return { success: false, error: `Error eliminando paquete: ${shipErr.message}` };
    }

    onDeleteShipment(item.id);
    onDeleteSuccess(`Paquete ${item.unique_package_id} eliminado.`);
    setShowDeleteModal(false);
    return { success: true };
  };

  console.log('[ManifestRow]', item.unique_package_id, 'recipient_name:', item.recipient_name, 'full_name:', item.customers?.full_name);
  return (
    <>
      <div className={`row-item ${expanded ? 'active' : ''}`} style={{ background: expanded ? 'var(--bg)' : '', flexDirection: 'column', alignItems: 'stretch' }}>
        {/* ── Collapsed row ── */}
        {/* ── Line 1: chevron + ID + SHIP/HOLD + type + spacer + PENDING + delete ── */}
        <div
          className="flex items-center gap-2 w-full cursor-pointer"
          onClick={() => !isEditing && setExpanded(!expanded)}
          style={{ padding: '4px 0 2px 0' }}
        >
          <div className="flex justify-center text-gray-400"
            style={{ width: '16px', flexShrink: 0 }}>
            {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          </div>
          <div className="row-id" style={{ flexShrink: 0 }}>
            {isCollision && (
              <span className="badge badge-error"
                style={{ marginRight: '4px' }}>!</span>
            )}
            {item.unique_package_id}
          </div>
          <span
            className={`badge ${item.shipping_status === 'Send Immediately'
              ? 'badge-ship' : 'badge-hold'}`}
            style={{ flexShrink: 0 }}
          >
            {item.shipping_status === 'Send Immediately' ? 'SHIP' : 'HOLD'}
          </span>
          <span
            className={`badge ${item.shipment_type === 'ENCOMIENDA'
              ? 'badge-enc' : 'badge-cas'}`}
            style={{ flexShrink: 0 }}
          >
            {item.shipment_type || 'CASILLERO'}
          </span>
          <div style={{ flex: 1 }} />
          <span
            className={`badge ${item.clientVersion
              ? 'badge-done' : 'badge-pending'}`}
            style={{ flexShrink: 0 }}
          >
            {item.clientVersion ? 'VERIFIED' : 'PENDING'}
          </span>
          {userPermissions?.canDeletePackages && (
            <button
              onClick={e => { e.stopPropagation(); setShowDeleteModal(true); }}
              className="btn-ghost"
              style={{ padding: '4px', color: 'var(--avianca-red)', flexShrink: 0 }}
              title="Eliminar paquete"
            >
              <Trash2 size={14}/>
            </button>
          )}
        </div>
        {/* ── Line 2: name left, date right ── */}
        <div
          className="flex items-center justify-between w-full"
          style={{ paddingLeft: '24px', paddingBottom: '4px' }}
          onClick={() => !isEditing && setExpanded(!expanded)}
        >
          <span style={{
            fontSize: '12px',
            fontWeight: '500',
            color: 'var(--text-secondary)',
          }}>
            {item.recipient_name || item.customers?.full_name
              || item.customer_id}
          </span>
          <span style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            flexShrink: 0,
            marginLeft: '8px',
          }}>
            {item.entry_timestamp
              ? new Date(item.entry_timestamp).toLocaleDateString()
              : '---'}
          </span>
        </div>

        {/* ── Expanded content ── */}
        {expanded && (
          <div className="p-4 border-t border-gray-100 animate-in slide-in-from-top-2" style={{ borderColor: 'var(--border)' }}>
            <div className="flex flex-col md:flex-row gap-6">
              {/* Info Column */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <p className="metric-label" style={{ marginBottom: 0 }}>Tracking Number</p>
                      {(item.warehouse_photo_url || item.photo_url) && !item.tracking_number && (
                        <button
                          type="button"
                          onClick={handleScanTracking}
                          disabled={scanningTracking}
                          style={{
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)',
                            background: 'var(--tile)',
                            color: 'var(--sky-blue)',
                            fontSize: '9px',
                            fontWeight: '700',
                            cursor: scanningTracking ? 'not-allowed' : 'pointer',
                            opacity: scanningTracking ? 0.6 : 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {scanningTracking ? '⏳...' : '📷 Leer'}
                        </button>
                      )}
                    </div>
                    <p className="text-sm font-mono mt-1" style={{ color: 'var(--text-main)' }}>
                      {item.tracking_number || '---'}
                    </p>
                    {scanTrackingError && (
                      <p style={{ fontSize: '9px', color: 'var(--avianca-red)', marginTop: '2px', fontWeight: '500' }}>
                        ⚠️ {scanTrackingError}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="metric-label">Shipping Status</p>
                    <div className="mt-1 flex gap-2">
                      <button
                        onClick={() => onUpdate(item.id, { shipping_status: 'Send Immediately' })}
                        className={`btn ${item.shipping_status === 'Send Immediately' ? 'btn-primary' : 'btn-secondary'}`}
                      >
                        SHIP
                      </button>
                      <button
                        onClick={() => onUpdate(item.id, { shipping_status: 'Postpone to Next Delivery' })}
                        className={`btn ${item.shipping_status === 'Postpone to Next Delivery' ? 'btn-primary' : 'btn-secondary'}`}
                        style={{ background: item.shipping_status === 'Postpone to Next Delivery' ? 'var(--amber)' : '' }}
                      >
                        HOLD
                      </button>
                    </div>
                  </div>
                </div>

                <div className="divider" />

                <ShipmentItemsList shipmentId={item.id} />

                <div className="flex gap-2">
                  <button onClick={startEdit} className="btn btn-secondary">
                    <Edit size={14}/> Edit Info
                  </button>
                  {userPermissions?.canUseAI && (
                    <button onClick={() => setAnalyzingShipmentId(item.id)} className="btn btn-secondary" style={{ color: 'var(--blue-text)', borderColor: 'var(--blue)' }}>
                      ✨ Analyze Receipt (AI)
                    </button>
                  )}
                  {isCollision && (
                    <button onClick={() => handleMerge(item.unique_package_id.replace('-DUP',''), item.id)} className="btn btn-primary" style={{ background: 'var(--amber)' }}>
                      Merge Duplicate
                    </button>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN — Photo + Receipts stacked, sits inside flex row */}
              <div style={{ width: '220px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>

                {/* Package photo */}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                    Foto del paquete
                  </div>
                  {/* Photo tile: click image → view full size, buttons below to download/change */}
                  <div className="tile relative overflow-hidden" style={{ height: '120px', padding: 0, maxWidth: '100%' }}>
                    {(item.warehouse_photo_url || item.photo_url) ? (
                      <a
                        href={item.warehouse_photo_url || item.photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block', width: '100%', height: '100%' }}
                        title="Ver foto completa"
                      >
                        <img
                          src={item.warehouse_photo_url || item.photo_url}
                          alt="Package"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </a>
                    ) : (
                      <div
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', cursor: 'pointer' }}
                        onClick={() => triggerPhotoUpload(item.id)}
                      >
                        <PlusCircle size={20} style={{ color: 'var(--border)', marginBottom: '4px' }} />
                        <span style={{ fontSize: '9px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                          Agregar foto
                        </span>
                      </div>
                    )}
                    <input
                      id={`photo-upload-${item.id}`}
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleManifestPhotoUpload(e, item)}
                    />
                  </div>
                  {/* Action row: Download + Change — only when photo exists */}
                  {(item.warehouse_photo_url || item.photo_url) && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                      <a
                        href={item.warehouse_photo_url || item.photo_url}
                        download
                        style={{
                          flex: 1,
                          padding: '5px 0',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--tile)',
                          color: 'var(--text-secondary)',
                          fontSize: '10px',
                          fontWeight: '700',
                          textAlign: 'center',
                          textDecoration: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        ⬇ Descargar
                      </a>
                      <button
                        type="button"
                        onClick={() => triggerPhotoUpload(item.id)}
                        style={{
                          flex: 1,
                          padding: '5px 0',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--tile)',
                          color: 'var(--text-secondary)',
                          fontSize: '10px',
                          fontWeight: '700',
                          cursor: 'pointer',
                        }}
                      >
                        📷 Cambiar
                      </button>
                    </div>
                  )}
                </div>

                {/* Receipts panel */}
                <PackageReceiptsPanel
                  shipment={item}
                  manifestId={item.manifest_id}
                  uploadedBy={user?.username || 'staff'}
                />

              </div>
            </div>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="tile w-full max-w-md animate-in zoom-in-95">
            <h3 className="page-title mb-4">Edit Package Info</h3>
            <div className="space-y-4">
              <div>
                <label className="metric-label">Tracking #</label>
                <input className="input mt-1" value={editForm.tracking_number || ''} onChange={e => setEditForm({...editForm, tracking_number: e.target.value})} />
              </div>
              <div>
                <label className="metric-label">Nombre del Remitente (EE.UU.)</label>
                <input
                  className="input mt-1"
                  placeholder="Nombre del remitente..."
                  value={editForm.recipient_name || ''}
                  onChange={e => setEditForm({ ...editForm, recipient_name: e.target.value })}
                />
              </div>
              <div>
                <label className="metric-label">Weight (lbs)</label>
                <input className="input mt-1" type="number" value={editForm.weight_lb || ''} onChange={e => setEditForm({...editForm, weight_lb: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={saveEdit} className="btn btn-primary flex-1 justify-center">Save Changes</button>
              <button onClick={() => setIsEditing(false)} className="btn btn-secondary flex-1 justify-center">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <DeletePackageModal
          item={item}
          user={user}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default LiveManifest;